import React, { createContext, useContext, useEffect, useState } from "react";

/**
 * Single source of truth for every user-facing string in the app.
 * Every visible label must go through `t("key")`. If a language doesn't
 * define a key we fall back to English.
 */
const STRINGS = {
  en: {
    // Brand
    appName: "Bethanie Haitian SDA Church",
    appNameShort: "Bethanie Haitian SDA",

    // Auth / registration
    chooseChurch: "Choose Your Church",
    chooseChurchSub: "Select your community to sign in",
    signIn: "Sign in",
    signInShort: "Sign in",
    signOut: "Sign out",
    register: "Create account",
    email: "Email",
    password: "Password",
    name: "Full name",
    phone: "Phone (optional)",
    welcome: "Welcome",
    guest: "guest",
    welcomeBack: "Welcome back",
    welcomeTo: "Welcome to",
    memberOrAdmin: "Member or admin",
    memberOrAdminDesc: "Enter your email & password",
    registerNewChurch: "Register a new church",
    registerNewChurchDesc: "Apply for approval — usually within 24 hours",
    superAdminAccess: "Super admin access",
    signInHint: "Choose how you want to access the app.",
    memberSignIn: "Member sign in",
    memberSignInSub: "RSVP, prayer wall, giving history & more",

    // Bottom nav / More
    home: "Home",
    bible: "Bible",
    giving: "Giving",
    songbook: "Song book",
    more: "More",

    // Feature labels
    announcements: "Announcements",
    events: "Events",
    sermons: "Sermons",
    yearbook: "Yearbook",
    board: "Church Board",
    pastor: "Pastor",
    firstElder: "First Elder",
    elders: "Elders",
    departments: "Departments",
    live: "Live Service",
    prayers: "Prayer Request",
    carpool: "Transportation",
    messages: "Calls & Messages",
    roster: "Roster",
    rosterDesc: "Pre-approve members by name so their sign-up is instant.",
    pendingSignups: "Pending sign-ups",
    pendingSignupsDesc: "Review sign-ups that didn't match the roster.",
    birthdayBannerSelf: "Happy birthday, {name}!",
    birthdayBannerOthers: "It's {name}'s birthday today — wish them well!",
    birthdayBannerMulti: "{count} birthdays today · tap to celebrate",
    birthdayWishTemplate: "Happy birthday, {name}! 🎂 Wishing you a joy-filled year ahead — praying God's richest blessings over you today. 🎉",
    birthdayLabel: "Birthday",
    birthdays: "Birthday wall",
    liveStartPush: "Live-service start push",
    birthdayCelebrantThankYou: "Thank you",
    birthdayCelebrantAppreciate: "I appreciate you",
    birthdayCelebrantHelp: "Say a quick thanks — tap one:",
    birthdayWallEmpty: "No one has a birthday today. Come back tomorrow!",
    notifDeleteSelected: "Delete selected",
    notifDeleteAll: "Delete all",
    notifSelectAll: "Select all",
    notifDeleteConfirm: "Delete {count} notification(s)?",
    notifDeleteAllConfirm: "Delete every notification? This cannot be undone.",
    notifDeletedToast: "Deleted",
    wishDeleteConfirm: "Delete this wish?",
    wishDeletedToast: "Wish deleted",
    audioTile: "Background music",
    audioTileDesc: "Upload hymns and worship tracks for the floating player.",
    sabbathSet: "Sabbath Set",
    sabbathSetHeading: "Sabbath Set List",
    sabbathSetSubtitle: "The songs, scriptures and order of service for this Sabbath.",
    sabbathSetNone: "No set list has been shared yet.",
    sabbathSetToday: "Today's Sabbath Set",
    sabbathSetUpcoming: "Upcoming Sabbaths",
    sabbathSetDraft: "Draft",
    sabbathSetPublished: "Published",
    sabbathSetPublish: "Publish",
    sabbathSetUnpublish: "Unpublish",
    sabbathSetAdd: "New set list",
    sabbathSetEditorTitle: "Sabbath set editor",
    sabbathSetItemHymn: "Hymn / Song",
    sabbathSetItemScripture: "Scripture",
    sabbathSetItemText: "Text item",
    sabbathSetItemPrayer: "Prayer",
    sabbathSetItemWelcome: "Welcome & Greetings",
    sabbathSetItemOffering: "Offering / Tithes",
    sabbathSetItemDoxology: "Doxology / Benediction",
    sabbathSetItemSpecialMusic: "Special Music",
    sabbathSetItemTestimony: "Testimony",
    sabbathSetItemChildrenStory: "Children's Story",
    sabbathSetItemResponsiveReading: "Responsive Reading",
    sabbathSetItemSermon: "Sermon",
    sabbathSetItemAnnouncement: "Announcements",
    sabbathSetItemSlides: "Slides / Presentation",
    sabbathSetOpenSlides: "Open slides",
    sabbathSetItemPerson: "Person / Leader (optional)",
    sabbathSetAddItem: "Add item",
    sabbathSetItemTitle: "Title",
    sabbathSetItemSubtitle: "Subtitle / hymn #",
    sabbathSetItemNotes: "Notes",
    sabbathSetDate: "Sabbath date",
    sabbathSetTheme: "Theme (optional)",
    sabbathSetTitleField: "Service title (optional)",
    sabbathSetNotesField: "Leader notes (optional)",
    sabbathSetEmptyItems: "Add hymns, scripture readings, or free-text items to build the flow.",
    sabbathSetShareCTA: "Open Sabbath Set",
    roleWorshipLeader: "Worship Leader",
    members: "Members",
    memberProfile: "Member profile",
    membersSearchPlaceholder: "Search members…",
    noMembersFound: "No members found.",
    memberNotFound: "Member not found.",
    sendMessage: "Send message",
    startNewChat: "Start new chat",
    browseMembersToDm: "Browse members and start a conversation",
    chatEmptyState: "No conversations yet. Message a member to get started.",
    offlineBanner: "You're offline — showing saved content.",
    conversations: "Conversations",
    yourDepartments: "Your departments",
    openChat: "Open chat",
    tapToJoinChat: "Tap to join group chat",
    noMessagesYet: "No messages yet.",
    messagePlaceholder: "Message…",
    messageSendFailed: "Could not send message.",
    uploadFailed: "Upload failed.",
    attachImage: "Attach image",
    muteSound: "Mute notification sound",
    unmuteSound: "Turn notification sound on",
    registerChurchByInviteOnly: "New churches are provisioned by invitation only. Contact your Church Hub administrator to get started.",
    isTyping: "is typing…",
    seenBy: "Seen by",
    membersLower: "members",
    member: "Member",
    chatNotFound: "This chat is unavailable.",
    backToChats: "Back to chats",
    all: "All",
    justNow: "just now",
    youPrefix: "You",
    back: "Back",
    notifications: "Notifications",
    settings: "Settings",
    language: "Language",
    registerHeading: "Create your church account",
    registerSubtitle: "A few details so we can find you on the members list.",
    finance: "Finance",
    membersAndRoles: "Members & Roles",
    branding: "Branding",
    subscription: "Subscription",

    // Descriptions used across landing/admin
    descAnnouncements: "Latest church news",
    descEvents: "Upcoming calendar",
    descSermons: "Recent messages",
    descSongbook: "Hymns & worship songs",
    descBoard: "Elders, admins, deacons",
    descPastor: "Meet our pastor",
    descFirstElder: "Meet our first elder",
    descDepartments: "Ministries & groups",
    descGiving: "Tithes, offerings & donations",
    descLive: "Watch worship service",

    // Landing
    heroSubtitle:
      "A Haitian Seventh-day Adventist community — worship, fellowship, and service. Welcome home.",
    sabbathLine1: "Sabbath",
    sabbathLine2: "Service",
    langChipHint: "Kreyòl · FR · EN",
    libraryChipHint: "Bible · Hymnals · Live",
    exploreEyebrow: "Explore",
    browseOurChurch: "Browse our church",
    noLoginRequired: "No login required",
    signInForMore: "Sign in for RSVP, prayer wall, personal Bible notes & giving history.",
    chooseLanguage: "Choose your language",
    openLibrary: "Open the church library",
    hymnalsLabel: "Hymnals",
    hymnalsDesc: "Chan Desperans & more",

    // Common actions
    submit: "Submit",
    cancel: "Cancel",
    chatSwipeHint: "Long-press or swipe left to delete",
    chatNSelected: "{n} selected",
    chatDeleteOne: "Conversation deleted",
    chatDeleteMany: "{n} conversations deleted",
    chatDeleteError: "Couldn't delete this conversation",
    deleteConversationTitle: "Delete this conversation?",
    deleteNConversationsTitle: "Delete {n} conversations?",
    deleteForMe: "Delete for me",
    deleteForMeHint: "Removes it from your inbox. A new message will bring it back.",
    deleteForEveryone: "Delete for everyone",
    deleteForEveryoneHint: "Removes the thread and all messages for every participant.",
    deleteForEveryoneUnavailable: "You don't have permission to delete this for everyone.",
    save: "Save",
    close: "Close",
    back: "Back",
    next: "Next",
    previous: "Previous",
    loading: "Loading…",
    search: "Search",
    edit: "Edit",
    delete: "Delete",
    confirm: "Confirm",
    yes: "Yes",
    no: "No",
    optional: "(optional)",
    all: "All",
    tapToRead: "Tap to read",

    // Home / Dashboard
    todayVerse: "Verse of the day",
    pinned: "Pinned",
    pinnedAnnouncements: "Pinned",
    quickActions: "Quick actions",
    upcomingServices: "Upcoming services",

    // Announcements
    noAnnouncements: "No announcements yet.",
    announcement: "Announcement",

    // Events
    upcoming: "Upcoming",
    upcomingTab: "Upcoming",
    allTab: "All",
    locationTBA: "Location TBA",
    goingLabel: "going",
    maybeLabel: "maybe",
    noEvents: "No events yet.",
    signInToRsvp: "Sign in to RSVP for this event.",

    // Sermons
    guestSpeaker: "Guest speaker",
    noSermons: "No sermons yet.",
    signInToPlay: "Sign in to play the audio/video.",
    searchSermons: "Search sermons…",

    // Songbook
    songSearch: "Search by title, number, or lyric…",
    songSearchShort: "Search title, number, lyric…",
    allHymnals: "All hymnals",
    hymnal: "Hymnal",
    noSongs: "No songs found.",
    noLyrics: "No lyrics available.",

    // Bible
    bibleSearch: "Search verses…",
    bibleSearchLong: "Search verses or reference…",
    bibleSearchPlaceholder: "Try 'John 3:16', 'Ps 23', or free text…",
    bibleSearchTip: "Tip: enter a reference like",
    bibleSearchTipExample: "to jump directly.",
    bibleNoteToSave: "Sign in to save personal verse notes.",
    bibleNote: "More chapters are loaded on demand. Try Psalm 23 or John 3.",
    noSearchResults: "No results. Try a reference like 'John 3:16' or 'Ps 23'.",
    searchFailed: "Search failed.",
    openingRef: "Opening",

    // Board / Pastor / Departments
    churchLeadership: "Church leadership",
    elders: "Elders",
    deacons: "Deacons",
    administrators: "Administrators",
    noBoard: "Board members will appear here.",
    noPastor: "Pastor profile hasn't been added yet.",
    noFirstElder: "First Elder profile hasn't been added yet.",
    seniorPastor: "Senior pastor",
    favoriteVerse: "Favorite verse",
    about: "About",
    submitPrayerRequest: "Submit a prayer request",
    sentPrivatelyPastor: "Sent privately to the pastor.",
    prayerBodyLabel: "Prayer",
    prayerSentToPastor: "Your prayer request was sent to the pastor.",
    prayerSendFailed: "Could not submit prayer request.",
    yourNameOptional: "Your name (optional)",
    yourNamePlaceholder: "Marie Joseph",
    aboutSection: "About",
    noBio: "No bio has been added yet.",
    pastorsMessage: "Pastor's message",
    ministryDepartment: "Ministry / department",
    memberSince: "Member since",
    appRole: "App role",
    noDepartments: "No departments yet.",
    memberCountSuffix: "member",
    membersCountSuffix: "members",

    // Live
    liveTitleFallback: "Live worship service",
    liveNotConfigured: "Live stream not configured yet.",
    openChannel: "Open channel on YouTube",
    serviceSchedule: "Service schedule",
    recentServices: "Recent services",
    watchOnYouTube: "Watch on YouTube",
    noLiveContent: "No live content available yet.",

    // Giving / Tithes
    giftMakesDifference: "Every gift makes a difference",
    givingIntro: "Secure giving via Stripe. No account needed — you'll receive a receipt by email.",
    yourInfoOptional: "Your info (optional)",
    yourName: "Your name",
    emailForReceipt: "Email (for receipt)",
    donorPrivacyNote:
      "Leave blank to give anonymously. Stripe will still email a receipt if you enter one at checkout.",
    tithesAndOfferings: "Tithes & Offerings",
    donation: "Donation",
    enterAmountHint: "Enter an amount for your Tithe, your Offering, or both.",
    tithe: "Tithe",
    offering: "Offering",
    totalToGive: "Total to give",
    noteOptional: "Note (optional)",
    notePlaceholder: "In memory of, dedication…",
    payTithe: "Give Tithe",
    payOffering: "Give Offering",
    custom: "Custom amount",
    continueToPayment: "Continue to secure payment",
    givePlaceholder: "Enter an amount to give",
    givingHistoryNote: "Sign in to see your giving history and receipts.",
    yourTransactions: "Your giving history",
    fund: "Fund",
    generalDonation: "General Donation",
    missions: "Missions",
    buildingFund: "Building Fund",
    amountUsd: "Amount (USD)",
    customAmount: "Custom amount",
    supportSpecificFunds: "Support specific ministry funds outside of your regular tithe.",
    redirecting: "Redirecting…",
    couldNotStartPayment: "Could not start payment.",
    enterAmountToast: "Please enter an amount for Tithe, Offering, or both.",

    // Payment result
    paymentSuccess: "Thank you for your gift!",
    paymentPending: "Confirming payment…",
    paymentFailed: "Payment did not complete",
    backToHome: "Back to home",
    tryAgain: "Try again",

    // Prayer
    askToPray: "I prayed",
    anonymous: "Post anonymously",
    forPastor: "Send to pastor only",
    prayerTitlePlaceholder: "Title (optional)",
    prayerBodyPlaceholder: "Share what's on your heart…",
    submitPrayer: "Submit prayer",

    // Departments taxonomy (used in Yearbook filters etc.)
    youth: "Youth",
    music: "Music",
    elderly: "Elderly",
    children: "Children",

    // Live states
    nowLive: "Live now",
    liveNow: "LIVE NOW",
    notLive: "Stream offline. Service replay available.",

    // Empty
    noResults: "No results",

    // Sign in card sub-lines
    signInSuffix: "Sign in to access.",

    // More dialog
    moreDialogTitle: "More",
    aboutUs: "About us",
    contactDeveloper: "Contact developer",
    contactTheDeveloper: "Send a message",
    phoneOptional: "Phone (optional)",
    aboutTheDeveloper: "About the developer",
    aboutTheDeveloperEyebrow: "About the developer",
    aboutTheDeveloperDesc: "Meet the person behind the app.",
    contactDeveloperTitle: "Built for your church",
    contactDeveloperSubtitle: "Ideas, bug reports, or a warm hello — the developer reads every message.",
    contactDeveloperPromise: "Most replies land within 48 hours.",
    contactDeveloperSuccess: "Message sent — thank you!",
    contactDeveloperError: "Something went wrong. Please try again in a moment.",
    contactDeveloperRateLimited: "You've sent a few messages recently — please try again in an hour.",
    contactDeveloperThankYou: "Message on its way!",
    contactDeveloperThankYouBody: "Your message reached the developer. Watch your inbox — a reply usually comes within 48 hours.",
    contactDeveloperSendAnother: "Send another",
    contactDeveloperSubjectPlaceholder: "e.g. Idea: birthday reminder emails",
    contactDeveloperMessagePlaceholder: "Share as much detail as helpful — screenshots can come via reply.",
    aboutTheApp: "About the app",
    yourName: "Your name",
    yourEmail: "Reply-to email",
    subject: "Subject",
    message: "Message",
    sending: "Sending…",
    aboutUsDesc: "Our story, mission & pastor",
    visitInPerson: "Visit us in person",
    visitInPersonDesc: "Address & service times",
    aboutOurChurch: "About our church",
    ourPastor: "Our pastor",
    openInMaps: "Open in Google Maps",
    noAddressYet: "Address not set yet — check back soon.",
    // Photo gallery
    gallery: "Church photos",
    galleryDesc: "Moments captured across our services & events",
    galleryStripCta: "Browse the gallery",
    galleryEmptyStrip: "No photos yet — check back soon.",
    albums: "Albums",
    noAlbumsYet: "No albums yet.",
    noPhotosYet: "No photos in this album yet.",
    createAlbum: "Create album",
    editAlbum: "Edit album",
    newAlbum: "New album",
    albumName: "Album name",
    albumDescription: "Description",
    albumCoverOptional: "Cover image (optional)",
    photosCount: "photos",
    downloadPhoto: "Download photo",
    downloadFailed: "Download failed. Try again.",
    voiceCall: "Call",
    voiceDirectCall: "Live audio call",
    voiceDeptRoom: "Department voice room",
    voiceConnecting: "Connecting…",
    voiceRinging: "Ringing…",
    voiceSpeaking: "Speaking",
    voiceInCall: "In call",
    voiceCallEnded: "Call ended by the other person",
    voiceNoAnswer: "No answer",
    broadcastEmpty: "Type a message to broadcast",
    broadcastSent: "Broadcast sent",
    voiceRoomWaiting: "You're the only one here. Waiting for others…",
    voiceInRoom: "in the room",
    voiceYou: "you",
    voiceMute: "Mute",
    voiceUnmute: "Unmute",
    voiceHangup: "Hang up",
    voiceEnableAudio: "Enable audio",
    voiceStartError: "Couldn't start the call. Try again.",
    voiceCalls: "Voice calls",
    voiceDirectFlag: "Voice · direct 1-to-1",
    voiceDeptFlag: "Voice · department rooms",
    voiceCallDisabledColumn: "Call",
    voiceCallDisabledTitle: "Can start calls",
    voiceRoomDisabledTitle: "Voice room",
    voiceMuteParticipant: "Mute participant",
    voiceUnmuteParticipant: "Unmute participant",
    voiceMuteFailed: "Couldn't update mute. Try again.",
    voiceStartCall: "Start call",
    voiceJoinCall: "Join call",
    voiceEndForAll: "End for all",
    voiceEndForAllConfirm: "End the call for everyone in this room?",
    voiceEnded: "Call ended for all",
    voiceInvite: "Invite",
    voiceInvitePickerTitle: "Invite a member to this call",
    voiceInviteSent: "Invite sent",
    voiceVolume: "Volume",
    broadcastLive: "Live broadcast",
    broadcastStart: "Start live broadcast",
    broadcastEnd: "End broadcast",
    broadcastEndConfirm: "End the live broadcast for everyone?",
    broadcastListenNow: "Tap to listen",
    broadcastListening: "You are listening",
    broadcastListeningNow: "listening",
    broadcastListenOnlyHint: "You can hear the speaker but not talk.",
    broadcastFromLower: "from",
    broadcastEnded: "Broadcast ended",
    adminTileOrder: "Grid layout",
    adminTileOrderDesc: "Re-order the tiles on public, member and admin grids.",
    adminTileOrderIntro: "Drag tiles up or down for each grid. New features you haven't sorted keep their default position at the end.",
    audienceAdmin: "Admin panel",
    gridOrderMoveUp: "Move up",
    gridOrderMoveDown: "Move down",
    gridOrderSave: "Save order",
    gridOrderNothingToSave: "Nothing to save",
    gridOrderSaved: "Grid order saved",
    voiceIncomingCall: "Incoming call",
    voiceUnknownCaller: "A member",
    voiceRingingSubtitle: "Live audio · tap to answer",
    voiceAnswer: "Answer",
    voiceDecline: "Decline",
    uploadPhoto: "Upload photo",
    uploadingPhoto: "Uploading…",
    pickAlbum: "Pick an album",
    photoCaption: "Caption (optional)",
    awaitingApproval: "Waiting for admin approval",
    permissionRequired: "Upload not allowed",
    permissionRequiredMsg: "Ask your main admin to grant you permission to upload photos.",
    moderationQueue: "Pending review",
    noPendingUploads: "No uploads waiting.",
    approve: "Approve",
    reject: "Reject",
    canUploadGallery: "Can upload gallery photos",
    canUploadGalleryHint: "Let this member submit photos to the church gallery (admin still approves each one).",
    featureVisibility: "Feature visibility",
    featureVisibilityIntro: "Show or hide any page from the public landing and from logged-in members. Missing entries default to visible — new features arrive on by default.",
    audiencePublic: "Public",
    audienceMembers: "Members",
    visibleFeatures: "features visible",
    showAll: "Show all",
    hideAll: "Hide all",
    feature: "Feature",
    publicShort: "Public",
    membersShort: "Members",
    saveVisibility: "Save visibility",
    featuresSaved: "Visibility saved · changes are live",
    saveFailed: "Failed to save",
    saving: "Saving…",
    adminDescFeatures: "Show / hide pages",
    boardTierElders: "Elders",
    boardTierSecretary: "Secretary",
    boardTierTreasurer: "Treasurer",
    boardTierAdministrators: "Administrators",
    boardTierDepartmentAdmins: "Department Administrators",
    autoFromDepartment: "Auto-added from department",
    boardAutoDeptHint: "Anyone set as a department administrator (Admin → Departments) is added to the Church Board automatically. Manage those seats from the Departments page.",
    auto: "Auto",
    weeklySchedule: "Weekly schedule",
    getInTouch: "Get in touch",
    sabbathLessons: "Sabbath Lessons",
    sabbathLessonsDesc: "Weekly SDA lessons",
    ssIntroduction: "Introduction",
    ssReadIntro: "Read the quarter introduction",
    ssLessons: "Weekly lessons",
    ssDay: "Day",
    ssBackToLessons: "Back to lessons",
    ssBackToLesson: "Back to lesson",
    ssNextDay: "Next",
    ssPrevDay: "Previous",
    ssCredits: "Credits",
    ssHtFallback: "Kreyòl translation not yet published for this quarter — showing French edition.",

    // ---------------- Admin & member UI (v2) ----------------
    // Role labels
    roleMainAdmin: "Main administrator",
    roleTreasurer: "Treasurer",
    roleElder: "Elder",
    roleFirstElder: "First Elder",
    rolePastor: "Pastor",
    roleDeptAdmin: "Dept admin",
    roleChurchSecretary: "Church Secretary",
    roleMember: "Member",

    // Dashboard shortcuts
    financeDashboard: "Finance dashboard",
    financeDashboardSub: "Tithes, offerings & analytics",
    openAdminPanel: "Open admin panel",
    openAdminPanelSub: "Manage church content, members & branding",

    // Admin panel
    adminPanel: "Admin panel",
    manageChurch: "Manage church",
    churchCode: "Church code",
    superAdmin: "Super admin",
    memberSignIn: "Member sign in",
    adminSignIn: "Admin sign in",
    memberSignInHelp: "Sign in with your email and password.",
    adminSignInHelp: "Manage your church — sign in with your admin account.",
    superAdminHelp: "Manage all churches on the platform.",
    modeMember: "I'm a member",
    modeAdmin: "I'm an admin",
    welcomeBack: "Welcome back",
    invalidCredentials: "Invalid credentials",
    newMember: "New member?",
    createAccount: "Create an account",
    noChurchYet: "No church yet?",
    registerChurch: "Register a church",
    departmentsCount: "Departments",
    actionRequired: "Action required",
    activateSubscription: "Activate a subscription so your members can use the app.",
    // Tile descriptions
    adminDescAnnouncements: "Create & schedule posts",
    adminDescEvents: "Calendar & RSVPs",
    adminDescSermons: "Upload or link audio/video",
    adminDescSongbook: "Add hymns & worship songs",
    adminDescBoard: "Elders, admins, deacons",
    adminDescPastor: "Bio, photo, message",
    adminDescFirstElder: "Bio, photo, message",
    adminDescDepartments: "Ministries & sub-admins",
    adminDescFinance: "Tithes, offerings & giving analytics",
    adminDescMembers: "Assign main/dept admins",
    adminDescBranding: "Name, colors, cover",
    adminDescSubscription: "Plan & billing",
    adminTileMembers: "Members & Roles",

    // Common CRUD verbs / statuses
    add: "Add",
    remove: "Remove",
    saving: "Saving…",
    saveChanges: "Save changes",
    submitPost: "Post",
    create: "Create",
    creating: "Adding…",
    deleteConfirm: "Delete?",
    done: "Done",
    copy: "Copy",
    copied: "Copied",

    // Members & profiles admin
    membersAndProfiles: "Members & profiles",
    searchMembers: "Search by name or email",
    membersEditHint: "Tap any member to edit their name, photo, bio, role and department. Tap Add to create a new member and issue them a temporary password.",
    addNewMember: "Add new member",
    editMemberProfile: "Edit member profile",
    memberEmailLabel: "Email",
    memberBirthDate: "Birth date",
    memberBio: "Bio",
    memberPhoto: "Photo",
    memberRole: "Role",
    memberDepartment: "Department",
    memberDepartments: "Departments",
    memberDeptNone: "None",
    memberProfileUpdated: "Profile updated",
    memberAdded: "Member added",
    tempPasswordOptional: "Temporary password (optional)",
    tempPasswordLeaveBlank: "Leave blank to auto-generate",
    memberCreated: "Member created",
    memberCreatedHint: "Share these one-time credentials with the new member. This password will only be shown once — copy it now.",
    copyCredentials: "Copy credentials",
    addMemberEssentials: "Fill in the essentials. Leave the password blank to auto-generate a temporary one.",
    fullNameRequired: "Full name *",
    emailRequired: "Email *",
    emailOptional: "Email (optional)",
    importMembersCsv: "Import members from CSV",
    csvHelp: "Upload a CSV file exported from Excel or Google Sheets. Only the 'name' column is required; every other column is optional. Rows with a duplicate email are skipped.",
    csvColumns: "Expected columns",
    csvDownloadTemplate: "Download a starter template",
    csvChooseFile: "Choose a CSV file",
    csvImport: "Import members",
    csvPickFile: "Please choose a CSV file first",
    csvNothingCreated: "No members were created — check the errors",
    csvImportFailed: "Import failed",
    csvCreated: "Created",
    csvSkipped: "Skipped",
    csvErrors: "Details",
    membersImported: "members imported",
    importing: "Importing…",
    select: "Select",
    selectAll: "Select all",
    selected: "selected",
    clearSelection: "Clear",
    deleteSelected: "Delete",
    deleting: "Deleting…",
    deleteMembersTitle: "Delete these members?",
    deleteMembersConfirm: "You are about to permanently remove {n} member(s) from your church.",
    deleteMembersWarning: "This cannot be undone. You cannot delete yourself, and the last main admin is always protected.",
    membersDeleted: "members deleted",
    deleteFailed: "Delete failed",

    // Announcements admin
    newAnnouncement: "New announcement",
    editAnnouncement: "Edit announcement",
    announcementTitle: "Title",
    announcementBody: "Body",
    coverImageOptional: "Cover image (optional)",
    targetDepartmentOptional: "Target department (optional)",
    allMembers: "All members",
    pinToTop: "Pin to top",
    pin: "Pin",
    unpin: "Unpin",
    deptPost: "Dept post",
    publicPost: "Public",
    noAnnouncementsYet: "No announcements yet.",
    updated: "Updated",
    posted: "Posted",

    // Events admin
    newEvent: "New event",
    editEvent: "Edit event",
    eventTitle: "Title",
    eventDescription: "Description",
    eventLocation: "Location",
    eventStartsAt: "Starts at",
    eventCreated: "Event created",
    eventUpdated: "Event updated",
    deleteEventConfirm: "Delete event?",
    noEventsYet: "No events yet.",
    going: "going",
    maybe: "maybe",
    cantAttend: "can't",

    // Sermons admin
    newSermon: "New sermon",
    editSermon: "Edit sermon",
    addSermon: "Add sermon",
    speaker: "Speaker",
    series: "Series",
    preachedOn: "Preached on",
    durationMin: "Duration (min)",
    scripture: "Scripture",
    mediaKind: "Media kind",
    kindYoutube: "YouTube video ID",
    kindVideoUrl: "Video URL (.mp4)",
    kindAudioUrl: "Audio URL (.mp3)",
    kindAudioFile: "Upload audio file",
    kindVideoFile: "Upload video file",
    youtubeIdLabel: "YouTube video ID (11 chars)",
    directUrl: "Direct URL",
    audioFile: "Audio file (.mp3)",
    videoFile: "Video file (.mp4)",
    chooseFile: "Choose file (max 200 MB)",
    uploading: "Uploading…",
    fileAttached: "✓ File attached",
    thumbnailOptional: "Thumbnail (optional)",
    sermonAdded: "Sermon added",
    sermonUpdated: "Sermon updated",
    deleteSermonConfirm: "Delete sermon?",
    noSermonsYet: "No sermons yet.",
    addMediaFirst: "Add a media link or upload a file",

    // Board admin
    churchBoard: "Church Board",
    addBoardMember: "Add board member",
    addToBoard: "Add to board",
    editBoardSeat: "Edit board seat",
    boardMemberSelect: "Choose member",
    boardTier: "Tier",
    boardRoleTitle: "Role title",
    boardOrder: "Order",
    noBoardYet: "No board members yet.",

    // Public prayer / carpool / yearbook
    submitPrayerCta: "Submit a prayer request",
    submitPrayerHint: "Sign in to share a private or public request",
    loadingPrayerWall: "Loading prayer wall…",
    noPrayersYet: "No prayer requests yet.",
    prayerLabel: "Prayer",
    prayingCount: "praying",
    carpoolCta: "Sign in to book or offer a ride",
    carpoolCtaHint: "Contact info is only shared with signed-in members",
    postRide: "Offer or request a ride",
    loadingCarpool: "Loading carpool board…",
    noCarpoolYet: "No open carpool posts.",
    rideOffers: "Ride offers",
    rideRequests: "Ride requests",
    offeringRide: "Offering ride",
    needsRide: "Needs a ride",
    seat: "seat",
    seats: "seats",
    yearbookSearch: "Search by name or department…",
    loadingYearbook: "Loading yearbook…",
    yearbookNoMatch: "No members match that search.",
    yearbookNoListed: "No members are publicly listed yet.",
    communityPrayerRequests: "Community prayer requests",
    shareRidesToService: "Share rides to service",
    churchFamilyDirectory: "Church family directory",
    prayerWall: "Prayer Request",

    // Layout / TopBar
    admin: "Admin",
    public: "Public",

    // Sermon player
    watch: "Watch",
    listen: "Listen",
  },

  fr: {
    appName: "Église Adventiste Haïtienne de Béthanie",
    appNameShort: "Béthanie Haïtienne ASJ",

    chooseChurch: "Choisissez votre Église",
    chooseChurchSub: "Sélectionnez votre communauté pour vous connecter",
    signIn: "Se connecter",
    signInShort: "Connexion",
    signOut: "Se déconnecter",
    register: "Créer un compte",
    email: "E-mail",
    password: "Mot de passe",
    name: "Nom complet",
    phone: "Téléphone (optionnel)",
    welcome: "Bienvenue",
    guest: "invité",
    welcomeBack: "Content de vous revoir",
    welcomeTo: "Bienvenue à",
    memberOrAdmin: "Membre ou admin",
    memberOrAdminDesc: "Entrez votre e-mail et mot de passe",
    registerNewChurch: "Inscrire une nouvelle église",
    registerNewChurchDesc: "Demande d'approbation — généralement sous 24 heures",
    superAdminAccess: "Accès super admin",
    signInHint: "Choisissez comment accéder à l'application.",
    memberSignIn: "Connexion membre",
    memberSignInSub: "RSVP, mur de prière, historique de dons et plus",

    home: "Accueil",
    bible: "Bible",
    giving: "Dons",
    songbook: "Livre de chant",
    more: "Plus",

    announcements: "Annonces",
    events: "Événements",
    sermons: "Prédications",
    yearbook: "Album",
    board: "Conseil",
    pastor: "Pasteur",
    firstElder: "Premier Ancien",
    elders: "Anciens",
    departments: "Départements",
    live: "En direct",
    prayers: "Demande de prière",
    carpool: "Transport",
    messages: "Appels et messages",
    sabbathSet: "Programme du Sabbat",
    sabbathSetHeading: "Programme du Sabbat",
    sabbathSetSubtitle: "Cantiques, lectures et ordre du culte pour ce Sabbat.",
    sabbathSetToday: "Programme d'aujourd'hui",
    sabbathSetUpcoming: "Sabbats à venir",
    sabbathSetNone: "Aucun programme partagé pour le moment.",
    sabbathSetShareCTA: "Voir le programme",
    sabbathSetItemHymn: "Cantique",
    sabbathSetItemScripture: "Lecture biblique",
    sabbathSetItemText: "Texte / note",
    sabbathSetItemPrayer: "Prière",
    sabbathSetItemWelcome: "Bienvenue & salutations",
    sabbathSetItemOffering: "Dîmes & offrandes",
    sabbathSetItemDoxology: "Doxologie / Bénédiction",
    sabbathSetItemSpecialMusic: "Musique spéciale",
    sabbathSetItemTestimony: "Témoignage",
    sabbathSetItemChildrenStory: "Histoire pour enfants",
    sabbathSetItemResponsiveReading: "Lecture responsoriale",
    sabbathSetItemSermon: "Sermon",
    sabbathSetItemAnnouncement: "Annonces",
    sabbathSetItemSlides: "Diapositives / Présentation",
    sabbathSetOpenSlides: "Ouvrir les diapositives",
    sabbathSetItemPerson: "Personne / responsable (optionnel)",
    roleWorshipLeader: "Chef de louange",
    members: "Membres",
    memberProfile: "Profil du membre",
    membersSearchPlaceholder: "Rechercher des membres…",
    noMembersFound: "Aucun membre trouvé.",
    memberNotFound: "Membre introuvable.",
    sendMessage: "Envoyer un message",
    startNewChat: "Nouvelle conversation",
    browseMembersToDm: "Parcourir les membres et démarrer une conversation",
    chatEmptyState: "Aucune conversation. Envoyez un message à un membre pour commencer.",
    offlineBanner: "Hors ligne — affichage du contenu enregistré.",
    conversations: "Conversations",
    yourDepartments: "Vos départements",
    openChat: "Ouvrir la conversation",
    tapToJoinChat: "Toucher pour rejoindre le groupe",
    noMessagesYet: "Aucun message pour l'instant.",
    messagePlaceholder: "Message…",
    messageSendFailed: "Impossible d'envoyer le message.",
    uploadFailed: "Échec du téléversement.",
    attachImage: "Joindre une image",
    muteSound: "Couper le son des notifications",
    unmuteSound: "Activer le son des notifications",
    registerChurchByInviteOnly: "Les nouvelles églises sont créées sur invitation uniquement. Contactez votre administrateur Church Hub pour commencer.",
    isTyping: "est en train d'écrire…",
    seenBy: "Vu par",
    membersLower: "membres",
    member: "Membre",
    chatNotFound: "Cette conversation n'est pas disponible.",
    backToChats: "Retour aux conversations",
    all: "Tous",
    justNow: "à l'instant",
    youPrefix: "Vous",
    back: "Retour",
    notifications: "Notifications",
    settings: "Paramètres",
    language: "Langue",
    birthdayWishTemplate: "Joyeux anniversaire, {name} ! 🎂 Que cette nouvelle année soit remplie de joie et des plus riches bénédictions du Seigneur. 🎉",
    birthdayLabel: "Anniversaire",
    birthdays: "Mur d'anniversaires",
    liveStartPush: "Notification de début du service en direct",
    birthdayCelebrantThankYou: "Merci",
    birthdayCelebrantAppreciate: "Je vous apprécie",
    birthdayCelebrantHelp: "Un petit merci — touchez un choix :",
    birthdayWallEmpty: "Personne ne fête son anniversaire aujourd'hui. Revenez demain !",
    notifDeleteSelected: "Supprimer la sélection",
    notifDeleteAll: "Tout supprimer",
    notifSelectAll: "Tout sélectionner",
    notifDeleteConfirm: "Supprimer {count} notification(s) ?",
    notifDeleteAllConfirm: "Tout supprimer ? Cette action est irréversible.",
    notifDeletedToast: "Supprimé",
    wishDeleteConfirm: "Supprimer ce message ?",
    wishDeletedToast: "Message supprimé",
    registerHeading: "Créez votre compte d'église",
    registerSubtitle: "Quelques informations pour vous retrouver sur la liste des membres.",
    finance: "Finances",
    membersAndRoles: "Membres et rôles",
    branding: "Identité",
    subscription: "Abonnement",

    descAnnouncements: "Dernières nouvelles",
    descEvents: "Prochain calendrier",
    descSermons: "Messages récents",
    descSongbook: "Cantiques et louanges",
    descBoard: "Anciens, admins, diacres",
    descPastor: "Rencontrez notre pasteur",
    descFirstElder: "Rencontrez notre premier ancien",
    descDepartments: "Ministères et groupes",
    descGiving: "Dîmes, offrandes et dons",
    descLive: "Regarder le culte en direct",

    heroSubtitle:
      "Une communauté Adventiste Haïtienne — louange, communion et service. Bienvenue chez nous.",
    sabbathLine1: "Culte",
    sabbathLine2: "du sabbat",
    langChipHint: "Kreyòl · FR · EN",
    libraryChipHint: "Bible · Chants · Direct",
    exploreEyebrow: "Explorer",
    browseOurChurch: "Découvrir notre église",
    noLoginRequired: "Sans connexion",
    signInForMore:
      "Connectez-vous pour RSVP, prière, notes de Bible personnelles et historique de dons.",
    chooseLanguage: "Choisissez votre langue",
    openLibrary: "Ouvrir la bibliothèque",
    hymnalsLabel: "Cantiques",
    hymnalsDesc: "Chan Desperans et plus",

    submit: "Envoyer",
    cancel: "Annuler",
    chatSwipeHint: "Appuyer long ou glisser à gauche pour supprimer",
    chatNSelected: "{n} sélectionné(s)",
    chatDeleteOne: "Conversation supprimée",
    chatDeleteMany: "{n} conversations supprimées",
    chatDeleteError: "Impossible de supprimer cette conversation",
    deleteConversationTitle: "Supprimer cette conversation ?",
    deleteNConversationsTitle: "Supprimer {n} conversations ?",
    deleteForMe: "Supprimer pour moi",
    deleteForMeHint: "Retire de votre boîte. Un nouveau message la fera revenir.",
    deleteForEveryone: "Supprimer pour tout le monde",
    deleteForEveryoneHint: "Supprime le fil et tous les messages pour chaque participant.",
    deleteForEveryoneUnavailable: "Vous n'avez pas l'autorisation.",
    save: "Enregistrer",
    close: "Fermer",
    back: "Retour",
    next: "Suivant",
    previous: "Précédent",
    loading: "Chargement…",
    search: "Rechercher",
    edit: "Modifier",
    delete: "Supprimer",
    confirm: "Confirmer",
    yes: "Oui",
    no: "Non",
    optional: "(optionnel)",
    all: "Tous",
    tapToRead: "Toucher pour lire",

    todayVerse: "Verset du jour",
    pinned: "Épinglé",
    pinnedAnnouncements: "Épinglées",
    quickActions: "Actions rapides",
    upcomingServices: "Prochains cultes",

    noAnnouncements: "Aucune annonce pour le moment.",
    announcement: "Annonce",

    upcoming: "À venir",
    upcomingTab: "À venir",
    allTab: "Tous",
    locationTBA: "Lieu à confirmer",
    goingLabel: "y vont",
    maybeLabel: "peut-être",
    noEvents: "Aucun événement pour le moment.",
    signInToRsvp: "Connectez-vous pour confirmer votre présence.",

    guestSpeaker: "Prédicateur invité",
    noSermons: "Aucune prédication pour le moment.",
    signInToPlay: "Connectez-vous pour écouter/regarder.",
    searchSermons: "Rechercher une prédication…",

    songSearch: "Rechercher chant, numéro, paroles…",
    songSearchShort: "Titre, numéro, paroles…",
    allHymnals: "Tous les recueils",
    hymnal: "Recueil",
    noSongs: "Aucun chant trouvé.",
    noLyrics: "Paroles non disponibles.",

    bibleSearch: "Rechercher un verset…",
    bibleSearchLong: "Rechercher verset ou référence…",
    bibleSearchPlaceholder: "Essayez « Jean 3:16 », « Ps 23 » ou texte libre…",
    bibleSearchTip: "Astuce : entrez une référence comme",
    bibleSearchTipExample: "pour y aller directement.",
    bibleNoteToSave: "Connectez-vous pour enregistrer vos notes personnelles.",
    bibleNote: "Plus de chapitres chargés à la demande. Essayez Psaume 23.",
    noSearchResults: "Aucun résultat. Essayez « Jean 3:16 » ou « Ps 23 ».",
    searchFailed: "La recherche a échoué.",
    openingRef: "Ouverture",

    churchLeadership: "Direction de l'église",
    elders: "Anciens",
    deacons: "Diacres",
    administrators: "Administrateurs",
    noBoard: "Les membres du conseil apparaîtront ici.",
    noPastor: "Le profil du pasteur n'a pas encore été ajouté.",
    noFirstElder: "Le profil du premier ancien n'a pas encore été ajouté.",
    seniorPastor: "Pasteur principal",
    favoriteVerse: "Verset préféré",
    about: "À propos",
    submitPrayerRequest: "Envoyer une demande de prière",
    sentPrivatelyPastor: "Envoyé en privé au pasteur.",
    prayerBodyLabel: "Prière",
    prayerSentToPastor: "Votre demande de prière a été envoyée au pasteur.",
    prayerSendFailed: "Impossible d'envoyer la demande de prière.",
    yourNameOptional: "Votre nom (facultatif)",
    yourNamePlaceholder: "Marie Joseph",
    aboutSection: "À propos",
    noBio: "Aucune biographie ajoutée.",
    pastorsMessage: "Message du pasteur",
    ministryDepartment: "Ministère / département",
    memberSince: "Membre depuis",
    appRole: "Rôle dans l'app",
    noDepartments: "Aucun département pour le moment.",
    memberCountSuffix: "membre",
    membersCountSuffix: "membres",

    liveTitleFallback: "Culte en direct",
    liveNotConfigured: "Diffusion en direct pas encore configurée.",
    openChannel: "Ouvrir la chaîne YouTube",
    serviceSchedule: "Horaire des cultes",
    recentServices: "Cultes récents",
    watchOnYouTube: "Regarder sur YouTube",
    noLiveContent: "Aucun contenu en direct disponible.",

    giftMakesDifference: "Chaque don compte",
    givingIntro: "Paiement sécurisé via Stripe. Aucun compte requis — un reçu vous sera envoyé par e-mail.",
    yourInfoOptional: "Vos coordonnées (optionnel)",
    yourName: "Votre nom",
    emailForReceipt: "E-mail (pour le reçu)",
    donorPrivacyNote:
      "Laisser vide pour donner anonymement. Stripe enverra tout de même un reçu si vous saisissez un e-mail au paiement.",
    tithesAndOfferings: "Dîmes et offrandes",
    donation: "Don",
    enterAmountHint: "Entrez un montant pour la dîme, l'offrande, ou les deux.",
    tithe: "Dîme",
    offering: "Offrande",
    totalToGive: "Total à donner",
    noteOptional: "Note (optionnel)",
    notePlaceholder: "À la mémoire de, dédicace…",
    payTithe: "Donner la dîme",
    payOffering: "Donner une offrande",
    custom: "Montant libre",
    continueToPayment: "Continuer vers le paiement",
    givePlaceholder: "Entrez un montant pour donner",
    givingHistoryNote: "Connectez-vous pour voir votre historique et vos reçus.",
    yourTransactions: "Vos dons",
    fund: "Fonds",
    generalDonation: "Don général",
    missions: "Missions",
    buildingFund: "Fonds de construction",
    amountUsd: "Montant (USD)",
    customAmount: "Montant personnalisé",
    supportSpecificFunds: "Soutenez des fonds spécifiques en plus de votre dîme.",
    redirecting: "Redirection…",
    couldNotStartPayment: "Impossible de démarrer le paiement.",
    enterAmountToast: "Entrez un montant pour la dîme, l'offrande, ou les deux.",

    paymentSuccess: "Merci pour votre don !",
    paymentPending: "Confirmation en cours…",
    paymentFailed: "Le paiement a échoué",
    backToHome: "Retour à l'accueil",
    tryAgain: "Réessayer",

    askToPray: "J'ai prié",
    anonymous: "Anonyme",
    forPastor: "Privé : pasteur",
    prayerTitlePlaceholder: "Titre (optionnel)",
    prayerBodyPlaceholder: "Partagez votre demande…",
    submitPrayer: "Envoyer prière",

    youth: "Jeunesse",
    music: "Musique",
    elderly: "Aînés",
    children: "Enfants",

    nowLive: "En direct",
    liveNow: "EN DIRECT",
    notLive: "Pas en direct. Replay disponible.",

    noResults: "Aucun résultat",
    signInSuffix: "Connectez-vous pour accéder.",
    moreDialogTitle: "Plus",
    aboutUs: "À propos",
    contactDeveloper: "Contacter le développeur",
    aboutTheDeveloper: "À propos du développeur",
    aboutTheDeveloperEyebrow: "À propos du développeur",
    aboutTheDeveloperDesc: "Rencontrez la personne derrière l'app.",
    contactDeveloperTitle: "Conçu pour votre église",
    contactDeveloperSubtitle: "Idées, bugs ou un simple bonjour — le développeur lit chaque message.",
    contactDeveloperPromise: "Les réponses arrivent en général sous 48 heures.",
    contactDeveloperSuccess: "Message envoyé — merci !",
    contactDeveloperError: "Un problème est survenu. Réessayez dans un instant.",
    contactDeveloperRateLimited: "Vous avez envoyé plusieurs messages récemment — réessayez dans une heure.",
    contactDeveloperThankYou: "Message en route !",
    contactDeveloperThankYouBody: "Votre message est arrivé chez le développeur. Une réponse arrive sous 48 heures.",
    contactDeveloperSendAnother: "Envoyer un autre",
    contactDeveloperSubjectPlaceholder: "ex. Idée : rappels d'anniversaires par email",
    contactDeveloperMessagePlaceholder: "Donnez autant de détails que possible — vous pouvez joindre des captures d'écran en réponse.",
    aboutTheApp: "À propos de l'app",
    yourName: "Votre nom",
    yourEmail: "E-mail de réponse",
    subject: "Sujet",
    message: "Message",
    sending: "Envoi…",
    aboutUsDesc: "Notre histoire, mission et pasteur",
    visitInPerson: "Nous rendre visite",
    visitInPersonDesc: "Adresse et horaires des services",
    aboutOurChurch: "À propos de notre église",
    ourPastor: "Notre pasteur",
    openInMaps: "Ouvrir dans Google Maps",
    noAddressYet: "Adresse pas encore définie — à bientôt.",
    gallery: "Photos de l'église",
    galleryDesc: "Moments de nos cultes et événements",
    galleryStripCta: "Parcourir la galerie",
    galleryEmptyStrip: "Pas encore de photos — revenez bientôt.",
    albums: "Albums",
    noAlbumsYet: "Aucun album pour le moment.",
    noPhotosYet: "Aucune photo dans cet album.",
    createAlbum: "Créer un album",
    editAlbum: "Modifier l'album",
    newAlbum: "Nouvel album",
    albumName: "Nom de l'album",
    albumDescription: "Description",
    albumCoverOptional: "Image de couverture (optionnel)",
    photosCount: "photos",
    downloadPhoto: "Télécharger la photo",
    downloadFailed: "Échec du téléchargement. Réessayez.",
    voiceCall: "Appel",
    voiceDirectCall: "Appel audio en direct",
    voiceDeptRoom: "Salon vocal du département",
    voiceConnecting: "Connexion…",
    voiceRinging: "Sonnerie…",
    voiceSpeaking: "En train de parler",
    voiceInCall: "En communication",
    voiceCallEnded: "Appel terminé par l'autre personne",
    voiceNoAnswer: "Pas de réponse",
    broadcastEmpty: "Écris un message à diffuser",
    broadcastSent: "Diffusion envoyée",
    voiceRoomWaiting: "Vous êtes seul(e) ici. En attente d'autres membres…",
    voiceInRoom: "dans le salon",
    voiceYou: "vous",
    voiceMute: "Muet",
    voiceUnmute: "Réactiver le micro",
    voiceHangup: "Raccrocher",
    voiceEnableAudio: "Activer l'audio",
    voiceStartError: "Impossible de démarrer l'appel. Réessayez.",
    voiceCalls: "Appels vocaux",
    voiceDirectFlag: "Vocal · appels 1 à 1",
    voiceDeptFlag: "Vocal · salons de département",
    voiceCallDisabledColumn: "Appel",
    voiceCallDisabledTitle: "Peut initier des appels",
    voiceRoomDisabledTitle: "Salon vocal",
    voiceMuteParticipant: "Couper le micro",
    voiceUnmuteParticipant: "Activer le micro",
    voiceMuteFailed: "Impossible de couper. Réessayez.",
    voiceStartCall: "Démarrer l'appel",
    voiceJoinCall: "Rejoindre l'appel",
    voiceEndForAll: "Terminer pour tous",
    voiceEndForAllConfirm: "Terminer l'appel pour tous les participants ?",
    voiceEnded: "Appel terminé pour tous",
    voiceInvite: "Inviter",
    voiceInvitePickerTitle: "Inviter un membre à l'appel",
    broadcastLive: "Diffusion en direct",
    broadcastStart: "Démarrer la diffusion",
    broadcastEnd: "Terminer la diffusion",
    broadcastEndConfirm: "Terminer la diffusion pour tout le monde ?",
    broadcastListenNow: "Appuyez pour écouter",
    broadcastListening: "Vous écoutez",
    broadcastListeningNow: "à l'écoute",
    broadcastListenOnlyHint: "Vous entendez l'intervenant mais ne pouvez pas parler.",
    broadcastFromLower: "de",
    broadcastEnded: "Diffusion terminée",
    voiceInviteSent: "Invitation envoyée",
    voiceVolume: "Volume",
    adminTileOrder: "Mise en page",
    adminTileOrderDesc: "Réorganiser les tuiles des grilles publiques, membres et admin.",
    adminTileOrderIntro: "Déplacez les tuiles vers le haut ou le bas pour chaque grille. Les nouveautés non triées gardent leur position par défaut à la fin.",
    audienceAdmin: "Panneau admin",
    gridOrderMoveUp: "Monter",
    gridOrderMoveDown: "Descendre",
    gridOrderSave: "Enregistrer l'ordre",
    gridOrderNothingToSave: "Rien à enregistrer",
    gridOrderSaved: "Ordre enregistré",
    voiceIncomingCall: "Appel entrant",
    voiceUnknownCaller: "Un membre",
    voiceRingingSubtitle: "Audio en direct · appuyez pour répondre",
    voiceAnswer: "Répondre",
    voiceDecline: "Refuser",
    uploadPhoto: "Ajouter une photo",
    uploadingPhoto: "Téléversement…",
    pickAlbum: "Choisissez un album",
    photoCaption: "Légende (optionnel)",
    awaitingApproval: "En attente d'approbation",
    permissionRequired: "Ajout non autorisé",
    permissionRequiredMsg: "Demandez à votre administrateur la permission d'ajouter des photos.",
    moderationQueue: "En attente de révision",
    noPendingUploads: "Aucune photo en attente.",
    approve: "Approuver",
    reject: "Rejeter",
    canUploadGallery: "Peut ajouter des photos",
    canUploadGalleryHint: "Autorise ce membre à proposer des photos à la galerie (l'administrateur approuve chacune).",
    featureVisibility: "Visibilité des fonctionnalités",
    featureVisibilityIntro: "Affichez ou masquez n'importe quelle page pour la page d'accueil publique et pour les membres connectés. Les valeurs manquantes s'affichent par défaut — les nouvelles fonctionnalités sont activées d'office.",
    audiencePublic: "Public",
    audienceMembers: "Membres",
    visibleFeatures: "fonctionnalités visibles",
    showAll: "Tout afficher",
    hideAll: "Tout masquer",
    feature: "Fonctionnalité",
    publicShort: "Public",
    membersShort: "Membres",
    saveVisibility: "Enregistrer la visibilité",
    featuresSaved: "Visibilité enregistrée · en direct",
    saveFailed: "Échec de l'enregistrement",
    saving: "Enregistrement…",
    adminDescFeatures: "Afficher / masquer les pages",
    boardTierElders: "Anciens",
    boardTierSecretary: "Secrétaire",
    boardTierTreasurer: "Trésorier",
    boardTierAdministrators: "Administrateurs",
    boardTierDepartmentAdmins: "Administrateurs de département",
    autoFromDepartment: "Ajouté automatiquement depuis le département",
    boardAutoDeptHint: "Toute personne définie comme administrateur d'un département (Admin → Départements) est automatiquement ajoutée au Conseil. Gérez ces sièges depuis la page Départements.",
    auto: "Auto",
    weeklySchedule: "Horaire hebdomadaire",
    getInTouch: "Nous contacter",
    sabbathLessons: "Leçons du Sabbat",
    sabbathLessonsDesc: "Leçons hebdomadaires",
    ssIntroduction: "Introduction",
    ssReadIntro: "Lire l'introduction du trimestre",
    ssLessons: "Leçons hebdomadaires",
    ssDay: "Jour",
    ssBackToLessons: "Retour aux leçons",
    ssBackToLesson: "Retour à la leçon",
    ssNextDay: "Suivant",
    ssPrevDay: "Précédent",
    ssCredits: "Crédits",
    ssHtFallback: "La traduction en Kreyòl n'est pas encore publiée pour ce trimestre — affichage de l'édition française.",

    // Admin & member UI (v2)
    roleMainAdmin: "Administrateur principal",
    roleTreasurer: "Trésorier",
    roleElder: "Ancien",
    roleFirstElder: "Premier ancien",
    rolePastor: "Pasteur",
    roleDeptAdmin: "Admin de dép.",
    roleChurchSecretary: "Secrétaire de l'église",
    roleMember: "Membre",
    financeDashboard: "Tableau de finances",
    financeDashboardSub: "Dîmes, offrandes et analyses",
    openAdminPanel: "Ouvrir le panneau admin",
    openAdminPanelSub: "Gérer le contenu, les membres et l'identité",
    adminPanel: "Panneau admin",
    manageChurch: "Gérer l'église",
    churchCode: "Code d'église",
    superAdmin: "Super administrateur",
    memberSignIn: "Connexion membre",
    adminSignIn: "Connexion admin",
    memberSignInHelp: "Connectez-vous avec votre e-mail et mot de passe.",
    adminSignInHelp: "Gérez votre église — connectez-vous en tant qu'admin.",
    superAdminHelp: "Gérez toutes les églises de la plateforme.",
    modeMember: "Je suis membre",
    modeAdmin: "Je suis admin",
    welcomeBack: "Bon retour",
    invalidCredentials: "Identifiants invalides",
    newMember: "Nouveau membre ?",
    createAccount: "Créer un compte",
    noChurchYet: "Pas encore d'église ?",
    registerChurch: "Enregistrer une église",
    departmentsCount: "Départements",
    actionRequired: "Action requise",
    activateSubscription: "Activez un abonnement pour que vos membres utilisent l'app.",
    adminDescAnnouncements: "Créer et planifier des posts",
    adminDescEvents: "Calendrier et RSVP",
    adminDescSermons: "Téléverser ou lier audio/vidéo",
    adminDescSongbook: "Ajouter des cantiques",
    adminDescBoard: "Anciens, admins, diacres",
    adminDescPastor: "Bio, photo, message",
    adminDescFirstElder: "Bio, photo, message",
    adminDescDepartments: "Ministères et sous-admins",
    adminDescFinance: "Dîmes, offrandes et analyses",
    adminDescMembers: "Attribuer les rôles",
    adminDescBranding: "Nom, couleurs, couverture",
    adminDescSubscription: "Formule et facturation",
    adminTileMembers: "Membres et rôles",
    add: "Ajouter",
    remove: "Retirer",
    saving: "Enregistrement…",
    saveChanges: "Enregistrer",
    submitPost: "Publier",
    create: "Créer",
    creating: "Ajout…",
    deleteConfirm: "Supprimer ?",
    done: "Terminé",
    copy: "Copier",
    copied: "Copié",
    membersAndProfiles: "Membres et profils",
    searchMembers: "Rechercher par nom ou e-mail",
    membersEditHint: "Touchez un membre pour modifier son nom, sa photo, sa bio, son rôle. Touchez Ajouter pour créer un nouveau membre avec un mot de passe temporaire.",
    addNewMember: "Ajouter un membre",
    editMemberProfile: "Modifier le profil",
    memberEmailLabel: "E-mail",
    memberBirthDate: "Date de naissance",
    memberBio: "Bio",
    memberPhoto: "Photo",
    memberRole: "Rôle",
    memberDepartment: "Département",
    memberDepartments: "Départements",
    memberDeptNone: "Aucun",
    memberProfileUpdated: "Profil mis à jour",
    memberAdded: "Membre ajouté",
    tempPasswordOptional: "Mot de passe temporaire (facultatif)",
    tempPasswordLeaveBlank: "Laisser vide pour générer",
    memberCreated: "Membre créé",
    memberCreatedHint: "Partagez ces identifiants avec le nouveau membre. Le mot de passe ne sera montré qu'une seule fois — copiez-le maintenant.",
    copyCredentials: "Copier les identifiants",
    addMemberEssentials: "Remplissez l'essentiel. Laissez le mot de passe vide pour le générer.",
    fullNameRequired: "Nom complet *",
    emailRequired: "E-mail *",
    emailOptional: "E-mail (facultatif)",
    importMembersCsv: "Importer des membres via CSV",
    csvHelp: "Téléversez un fichier CSV exporté depuis Excel ou Google Sheets. Seule la colonne 'name' est obligatoire. Les lignes dont l'e-mail existe déjà sont ignorées.",
    csvColumns: "Colonnes attendues",
    csvDownloadTemplate: "Télécharger un modèle",
    csvChooseFile: "Choisir un fichier CSV",
    csvImport: "Importer les membres",
    csvPickFile: "Veuillez choisir un fichier CSV",
    csvNothingCreated: "Aucun membre créé — voir les erreurs",
    csvImportFailed: "Échec de l'importation",
    csvCreated: "Créés",
    csvSkipped: "Ignorés",
    csvErrors: "Détails",
    membersImported: "membres importés",
    importing: "Importation…",
    select: "Sélectionner",
    selectAll: "Tout sélectionner",
    selected: "sélectionné(s)",
    clearSelection: "Effacer",
    deleteSelected: "Supprimer",
    deleting: "Suppression…",
    deleteMembersTitle: "Supprimer ces membres ?",
    deleteMembersConfirm: "Vous êtes sur le point de supprimer définitivement {n} membre(s) de votre église.",
    deleteMembersWarning: "Cette action est irréversible. Vous ne pouvez pas vous supprimer vous-même, et le dernier administrateur principal est toujours protégé.",
    membersDeleted: "membres supprimés",
    deleteFailed: "Échec de la suppression",
    newAnnouncement: "Nouvelle annonce",
    editAnnouncement: "Modifier l'annonce",
    announcementTitle: "Titre",
    announcementBody: "Contenu",
    coverImageOptional: "Image de couverture (facultatif)",
    targetDepartmentOptional: "Département cible (facultatif)",
    allMembers: "Tous les membres",
    pinToTop: "Épingler en haut",
    pin: "Épingler",
    unpin: "Désépingler",
    deptPost: "Post dép.",
    publicPost: "Publique",
    noAnnouncementsYet: "Aucune annonce.",
    updated: "Mise à jour",
    posted: "Publiée",
    newEvent: "Nouvel événement",
    editEvent: "Modifier l'événement",
    eventTitle: "Titre",
    eventDescription: "Description",
    eventLocation: "Lieu",
    eventStartsAt: "Débute à",
    eventCreated: "Événement créé",
    eventUpdated: "Événement mis à jour",
    deleteEventConfirm: "Supprimer l'événement ?",
    noEventsYet: "Aucun événement.",
    going: "présents",
    maybe: "peut-être",
    cantAttend: "absents",
    newSermon: "Nouveau sermon",
    editSermon: "Modifier le sermon",
    addSermon: "Ajouter un sermon",
    speaker: "Prédicateur",
    series: "Série",
    preachedOn: "Prêché le",
    durationMin: "Durée (min)",
    scripture: "Passage",
    mediaKind: "Type de média",
    kindYoutube: "ID YouTube",
    kindVideoUrl: "URL vidéo (.mp4)",
    kindAudioUrl: "URL audio (.mp3)",
    kindAudioFile: "Fichier audio",
    kindVideoFile: "Fichier vidéo",
    youtubeIdLabel: "ID vidéo YouTube (11 car.)",
    directUrl: "URL directe",
    audioFile: "Fichier audio (.mp3)",
    videoFile: "Fichier vidéo (.mp4)",
    chooseFile: "Choisir un fichier (max 200 Mo)",
    uploading: "Téléversement…",
    fileAttached: "✓ Fichier attaché",
    thumbnailOptional: "Miniature (facultatif)",
    sermonAdded: "Sermon ajouté",
    sermonUpdated: "Sermon mis à jour",
    deleteSermonConfirm: "Supprimer le sermon ?",
    noSermonsYet: "Aucun sermon.",
    addMediaFirst: "Ajoutez un lien ou un fichier",
    churchBoard: "Conseil d'église",
    addBoardMember: "Ajouter au conseil",
    addToBoard: "Ajouter au conseil",
    editBoardSeat: "Modifier le siège",
    boardMemberSelect: "Choisir un membre",
    boardTier: "Niveau",
    boardRoleTitle: "Fonction",
    boardOrder: "Ordre",
    noBoardYet: "Aucun membre du conseil.",
    submitPrayerCta: "Soumettre une demande de prière",
    submitPrayerHint: "Connectez-vous pour partager une demande",
    loadingPrayerWall: "Chargement du mur de prière…",
    noPrayersYet: "Aucune demande de prière.",
    prayerLabel: "Prière",
    prayingCount: "en prière",
    carpoolCta: "Connectez-vous pour offrir un trajet",
    carpoolCtaHint: "Les contacts sont partagés uniquement avec les membres connectés",
    postRide: "Proposer ou demander un trajet",
    loadingCarpool: "Chargement du covoiturage…",
    noCarpoolYet: "Aucune offre de covoiturage.",
    rideOffers: "Trajets proposés",
    rideRequests: "Demandes de trajet",
    offeringRide: "Propose un trajet",
    needsRide: "Cherche un trajet",
    seat: "place",
    seats: "places",
    yearbookSearch: "Rechercher par nom ou département…",
    loadingYearbook: "Chargement de l'album…",
    yearbookNoMatch: "Aucun membre ne correspond.",
    yearbookNoListed: "Aucun membre publié pour l'instant.",
    communityPrayerRequests: "Demandes de prière de la communauté",
    shareRidesToService: "Partager des trajets vers le culte",
    churchFamilyDirectory: "Annuaire de la famille",
    prayerWall: "Demande de prière",
    admin: "Admin",
    public: "Public",
    watch: "Regarder",
    listen: "Écouter",
  },

  ht: {
    appName: "Legliz Adventis Ayisyen Betani",
    appNameShort: "Betani Ayisyen ASJ",

    chooseChurch: "Chwazi legliz ou",
    chooseChurchSub: "Chwazi kominote ou pou konekte",
    signIn: "Konekte",
    signInShort: "Konekte",
    signOut: "Soti",
    register: "Kreye kont",
    email: "Imèl",
    password: "Modpas",
    name: "Non konplè",
    phone: "Telefòn (opsyonèl)",
    welcome: "Byenveni",
    guest: "vizitè",
    welcomeBack: "Byenveni ankò",
    welcomeTo: "Byenveni nan",
    memberOrAdmin: "Manm oswa admin",
    memberOrAdminDesc: "Antre imèl ou ak modpas",
    registerNewChurch: "Anrejistre yon nouvo legliz",
    registerNewChurchDesc: "Aplike pou apwobasyon — jeneralman anvan 24 èdtan",
    superAdminAccess: "Aksè Super Admin",
    signInHint: "Chwazi kijan ou vle antre nan app la.",
    memberSignIn: "Konekte manm",
    memberSignInSub: "RSVP, mi lapriyè, istwa don ak plis",

    home: "Akèy",
    bible: "Bib",
    giving: "Don",
    songbook: "Liv chante",
    more: "Plis",

    announcements: "Anons",
    events: "Evènman",
    sermons: "Predikasyon",
    yearbook: "Liv Manb",
    board: "Konsèy",
    pastor: "Pastè",
    firstElder: "Premye Ansyen",
    elders: "Ansyen yo",
    departments: "Depatman",
    live: "Dirèk",
    prayers: "Demand priyè",
    carpool: "Transpò",
    sabbathSet: "Pwogram Saba a",
    sabbathSetHeading: "Pwogram Saba a",
    sabbathSetSubtitle: "Kantik, lekti ak lòd sèvis pou Saba a.",
    sabbathSetToday: "Pwogram jodi a",
    sabbathSetUpcoming: "Saba k ap vini yo",
    sabbathSetNone: "Poko gen okenn pwogram pataje.",
    sabbathSetShareCTA: "Louvri pwogram nan",
    sabbathSetItemHymn: "Kantik",
    sabbathSetItemScripture: "Lekti biblik",
    sabbathSetItemText: "Tèks / nòt",
    sabbathSetItemPrayer: "Priyè",
    sabbathSetItemWelcome: "Byenveni & salitasyon",
    sabbathSetItemOffering: "Dim ak Ofrand",
    sabbathSetItemDoxology: "Doksoloji / Benediksyon",
    sabbathSetItemSpecialMusic: "Mizik espesyal",
    sabbathSetItemTestimony: "Temwayaj",
    sabbathSetItemChildrenStory: "Istwa pou timoun",
    sabbathSetItemResponsiveReading: "Lekti antre kongregasyon an",
    sabbathSetItemSermon: "Sèmon",
    sabbathSetItemAnnouncement: "Anons yo",
    sabbathSetItemSlides: "Prezantasyon / Slid",
    sabbathSetOpenSlides: "Louvri slid yo",
    sabbathSetItemPerson: "Moun / responsab (opsyonèl)",
    roleWorshipLeader: "Chèf louwanj",
    messages: "Apèl ak Mesaj",
    members: "Manm",
    memberProfile: "Pwofil manm",
    membersSearchPlaceholder: "Chèche manm…",
    noMembersFound: "Pa gen manm ki jwenn.",
    memberNotFound: "Manm pa jwenn.",
    sendMessage: "Voye yon mesaj",
    startNewChat: "Nouvo chat",
    browseMembersToDm: "Gade manm yo epi kòmanse yon chat",
    chatEmptyState: "Poko gen chat. Voye yon mesaj bay yon manm pou kòmanse.",
    offlineBanner: "Ou pa konekte — n ap montre kontni ki sove a.",
    conversations: "Konvèsasyon",
    yourDepartments: "Depatman ou yo",
    openChat: "Louvri chat",
    tapToJoinChat: "Klike pou antre nan gwoup la",
    noMessagesYet: "Poko gen mesaj.",
    messagePlaceholder: "Mesaj…",
    messageSendFailed: "Pa ka voye mesaj la.",
    uploadFailed: "Chajman echwe.",
    attachImage: "Mete yon imaj",
    muteSound: "Fèmen son notifikasyon",
    unmuteSound: "Ouvri son notifikasyon",
    registerChurchByInviteOnly: "Nouvo legliz yo enskri sèlman sou envitasyon. Kontakte administratè Church Hub ou pou kòmanse.",
    isTyping: "ap ekri…",
    seenBy: "Wè pa",
    membersLower: "manm",
    member: "Manm",
    chatNotFound: "Chat sa a pa disponib.",
    backToChats: "Tounen nan chat yo",
    all: "Tout",
    justNow: "kounye a",
    youPrefix: "Ou",
    back: "Retounen",
    notifications: "Notifikasyon",
    settings: "Paramèt",
    language: "Lang",
    birthdayWishTemplate: "Bòn fèt, {name} ! 🎂 M ap swete w yon ane ki chaje ak lajwa ak benediksyon Bondye. 🎉",
    birthdayLabel: "Anivèsè",
    birthdays: "Miray anivèsè",
    liveStartPush: "Notifikasyon lè sèvis an direk kòmanse",
    birthdayCelebrantThankYou: "Mèsi",
    birthdayCelebrantAppreciate: "M apresye w",
    birthdayCelebrantHelp: "Yon ti mèsi rapid — peze youn :",
    birthdayWallEmpty: "Pesòn pa gen anivèsè jodi a. Retounen demen !",
    notifDeleteSelected: "Efase seleksyon",
    notifDeleteAll: "Efase tout",
    notifSelectAll: "Chwazi tout",
    notifDeleteConfirm: "Efase {count} notifikasyon?",
    notifDeleteAllConfirm: "Efase tout notifikasyon? Sa pa ka defèt.",
    notifDeletedToast: "Efase",
    wishDeleteConfirm: "Efase mesaj sa a ?",
    wishDeletedToast: "Mesaj efase",
    registerHeading: "Kreye kont legliz ou",
    registerSubtitle: "Kèk enfòmasyon pou nou ka jwenn ou nan lis manm yo.",
    finance: "Finans",
    membersAndRoles: "Manm ak Wòl",
    branding: "Idantite",
    subscription: "Abònman",

    descAnnouncements: "Dènye nouvèl legliz la",
    descEvents: "Pwochen kalandriye",
    descSermons: "Mesaj resan",
    descSongbook: "Chan ak louwanj",
    descBoard: "Ansyen, admin, dyak",
    descPastor: "Rankontre pastè nou",
    descFirstElder: "Rankontre premye ansyen nou",
    descDepartments: "Ministè ak gwoup",
    descGiving: "Ladim, òfrann ak don",
    descLive: "Gade sèvis kilt la",

    heroSubtitle:
      "Yon kominote Adventis Ayisyen — kilt, konpanyi ak sèvis. Byenveni lakay ou.",
    sabbathLine1: "Sèvis",
    sabbathLine2: "Saba",
    langChipHint: "Kreyòl · FR · EN",
    libraryChipHint: "Bib · Chante · Dirèk",
    exploreEyebrow: "Eksplore",
    browseOurChurch: "Dekouvri legliz nou",
    noLoginRequired: "Pa bezwen konekte",
    signInForMore:
      "Konekte pou RSVP, mi lapriyè, nòt Bib pèsonèl, ak istwa don ou.",
    chooseLanguage: "Chwazi lang ou",
    openLibrary: "Louvri bibliyotèk legliz la",
    hymnalsLabel: "Chan",
    hymnalsDesc: "Chan Desperans ak plis",

    submit: "Voye",
    cancel: "Anile",
    chatSwipeHint: "Peze long oswa glise a goch pou efase",
    chatNSelected: "{n} chwazi",
    chatDeleteOne: "Konvèsasyon efase",
    chatDeleteMany: "{n} konvèsasyon efase",
    chatDeleteError: "Pa t' kapab efase konvèsasyon sa a",
    deleteConversationTitle: "Efase konvèsasyon sa a?",
    deleteNConversationsTitle: "Efase {n} konvèsasyon?",
    deleteForMe: "Efase pou mwen",
    deleteForMeHint: "Retire l' nan bwat mwen. Yon nouvo mesaj ap fè l' tounen.",
    deleteForEveryone: "Efase pou tout moun",
    deleteForEveryoneHint: "Efase tout mesaj yo pou chak moun.",
    deleteForEveryoneUnavailable: "Ou pa gen otorizasyon.",
    save: "Sove",
    close: "Fèmen",
    back: "Retounen",
    next: "Pwochen",
    previous: "Anvan",
    loading: "Chaje…",
    search: "Chèche",
    edit: "Modifye",
    delete: "Efase",
    confirm: "Konfime",
    yes: "Wi",
    no: "Non",
    optional: "(opsyonèl)",
    all: "Tout",
    tapToRead: "Peze pou li",

    todayVerse: "Vèsè jou a",
    pinned: "Fikse",
    pinnedAnnouncements: "Fikse",
    quickActions: "Aksyon rapid",
    upcomingServices: "Pwochen sèvis",

    noAnnouncements: "Poko gen anons.",
    announcement: "Anons",

    upcoming: "Pwochen",
    upcomingTab: "Pwochen",
    allTab: "Tout",
    locationTBA: "Kote a ap konfime pita",
    goingLabel: "prale",
    maybeLabel: "petèt",
    noEvents: "Poko gen evènman.",
    signInToRsvp: "Konekte pou fè RSVP pou evènman sa a.",

    guestSpeaker: "Predikatè envite",
    noSermons: "Poko gen predikasyon.",
    signInToPlay: "Konekte pou ekoute/gade.",
    searchSermons: "Chèche predikasyon…",

    songSearch: "Chèche pa tit, nimewo, oswa pawòl…",
    songSearchShort: "Tit, nimewo, pawòl…",
    allHymnals: "Tout liv chante",
    hymnal: "Liv chante",
    noSongs: "Pa gen chante ki jwenn.",
    noLyrics: "Pa gen pawòl disponib.",

    bibleSearch: "Chèche vèsè…",
    bibleSearchLong: "Chèche vèsè oswa referans…",
    bibleSearchPlaceholder: "Eseye « Jan 3:16 », « Ps 23 » oswa tèks lib…",
    bibleSearchTip: "Konsèy : antre yon referans tankou",
    bibleSearchTipExample: "pou ale dirèkteman.",
    bibleNoteToSave: "Konekte pou sove nòt pèsonèl ou.",
    bibleNote: "Plis chapit yo chaje sou demand.",
    noSearchResults: "Pa gen rezilta. Eseye « Jan 3:16 » oswa « Ps 23 ».",
    searchFailed: "Rechèch la echwe.",
    openingRef: "Ap louvri",

    churchLeadership: "Lidèchip legliz la",
    elders: "Ansyen",
    deacons: "Dyak",
    administrators: "Administratè",
    noBoard: "Manm konsèy la ap parèt isit la.",
    noPastor: "Pwofil pastè a poko ajoute.",
    noFirstElder: "Pwofil premye ansyen an poko ajoute.",
    seniorPastor: "Pastè prensipal",
    favoriteVerse: "Vèsè pi renmen",
    about: "Konsènan",
    submitPrayerRequest: "Voye yon demand priyè",
    sentPrivatelyPastor: "Voye an prive bay pastè a.",
    prayerBodyLabel: "Priyè",
    prayerSentToPastor: "Demand priyè ou a voye bay pastè a.",
    prayerSendFailed: "Nou pa t ka voye demand priyè a.",
    yourNameOptional: "Non ou (opsyonèl)",
    yourNamePlaceholder: "Marie Joseph",
    aboutSection: "Sou li",
    noBio: "Pa gen biyografi ki ajoute.",
    pastorsMessage: "Mesaj pastè a",
    ministryDepartment: "Ministè / depatman",
    memberSince: "Manm depi",
    appRole: "Wòl nan app la",
    noDepartments: "Poko gen depatman.",
    memberCountSuffix: "manm",
    membersCountSuffix: "manm",

    liveTitleFallback: "Kilt an dirèk",
    liveNotConfigured: "Difizyon dirèk poko konfigire.",
    openChannel: "Louvri chanèl YouTube",
    serviceSchedule: "Orè kilt yo",
    recentServices: "Kilt resan",
    watchOnYouTube: "Gade sou YouTube",
    noLiveContent: "Poko gen kontni dirèk.",

    giftMakesDifference: "Chak don konte",
    givingIntro: "Peyman sekirize atravè Stripe. Pa bezwen kont — w ap resevwa yon resi pa imèl.",
    yourInfoOptional: "Enfòmasyon ou (opsyonèl)",
    yourName: "Non ou",
    emailForReceipt: "Imèl (pou resi)",
    donorPrivacyNote:
      "Kite vid pou bay anonim. Stripe ap toujou voye yon resi si ou antre yon imèl pandan peyman an.",
    tithesAndOfferings: "Ladim ak òfrann",
    donation: "Don",
    enterAmountHint: "Antre yon montan pou ladim ou, òfrann ou, oswa tou de.",
    tithe: "Ladim",
    offering: "Òfrann",
    totalToGive: "Total pou bay",
    noteOptional: "Nòt (opsyonèl)",
    notePlaceholder: "Nan memwa, dedikas…",
    payTithe: "Bay ladim",
    payOffering: "Bay òfrann",
    custom: "Montan pèsonalize",
    continueToPayment: "Kontinye nan peyman sekirize",
    givePlaceholder: "Antre yon montan pou bay",
    givingHistoryNote: "Konekte pou wè istwa ak resi don ou.",
    yourTransactions: "Istwa don ou",
    fund: "Fon",
    generalDonation: "Don Jeneral",
    missions: "Misyon",
    buildingFund: "Fon Bilding",
    amountUsd: "Montan (USD)",
    customAmount: "Montan pèsonalize",
    supportSpecificFunds: "Sipòte fon ministè espesifik anplis ladim ou.",
    redirecting: "Ap redireksyon…",
    couldNotStartPayment: "Pa kapab kòmanse peyman an.",
    enterAmountToast: "Antre yon montan pou ladim, òfrann, oswa tou de.",

    paymentSuccess: "Mèsi pou don ou!",
    paymentPending: "Konfimasyon…",
    paymentFailed: "Peyman an pa pase",
    backToHome: "Retounen akèy",
    tryAgain: "Eseye ankò",

    askToPray: "Mwen priye",
    anonymous: "Anonim",
    forPastor: "Sèlman pou pastè",
    prayerTitlePlaceholder: "Tit (opsyonèl)",
    prayerBodyPlaceholder: "Pataje demand ou…",
    submitPrayer: "Voye lapriyè",

    youth: "Jenès",
    music: "Mizik",
    elderly: "Granmoun",
    children: "Timoun",

    nowLive: "An dirèk",
    liveNow: "AN DIRÈK",
    notLive: "Pa an dirèk. Replay disponib.",

    noResults: "Pa gen rezilta",
    signInSuffix: "Konekte pou aksede.",
    moreDialogTitle: "Plis",
    aboutUs: "Konnen nou",
    contactDeveloper: "Kontakte devlopè a",
    aboutTheDeveloper: "Konnen devlopè a",
    aboutTheDeveloperEyebrow: "Sou devlopè a",
    aboutTheDeveloperDesc: "Rankontre moun ki bati aplikasyon an.",
    contactDeveloperTitle: "Bati pou legliz ou",
    contactDeveloperSubtitle: "Ide, bug, oswa yon salitasyon — devlopè a li chak mesaj.",
    contactDeveloperPromise: "Repons yo pran maks 48 èdtan.",
    contactDeveloperSuccess: "Mesaj voye — mèsi !",
    contactDeveloperError: "Yon bagay pa mache. Eseye ankò nan yon moman.",
    contactDeveloperRateLimited: "Ou voye kèk mesaj resamman — eseye ankò nan yon èdtan.",
    contactDeveloperThankYou: "Mesaj sou wout !",
    contactDeveloperThankYouBody: "Mesaj ou rive chez devlopè a. Yon repons ap vini nan 48 èdtan.",
    contactDeveloperSendAnother: "Voye yon lòt",
    contactDeveloperSubjectPlaceholder: "egz. Ide : rapèl anivèsè pa imèl",
    contactDeveloperMessagePlaceholder: "Bay tout detay ou kapab — foto ka vini pa repons.",
    aboutTheApp: "Sou aplikasyon an",
    yourName: "Non ou",
    yourEmail: "Imèl pou repons",
    subject: "Sijè",
    message: "Mesaj",
    sending: "Voye…",
    aboutUsDesc: "Istwa nou, misyon ak pastè",
    visitInPerson: "Vizite nou an pèsòn",
    visitInPersonDesc: "Adrès ak orè sèvis yo",
    aboutOurChurch: "Konsènan legliz nou an",
    ourPastor: "Pastè nou an",
    openInMaps: "Ouvri nan Google Maps",
    noAddressYet: "Adrès la poko defini — tounen byento.",
    gallery: "Foto legliz",
    galleryDesc: "Moman nan sèvis ak evènman nou yo",
    galleryStripCta: "Wè galri a",
    galleryEmptyStrip: "Poko gen foto — tounen byento.",
    albums: "Albòm",
    noAlbumsYet: "Poko gen albòm.",
    noPhotosYet: "Pa gen foto nan albòm sa a.",
    createAlbum: "Kreye albòm",
    editAlbum: "Modifye albòm",
    newAlbum: "Nouvo albòm",
    albumName: "Non albòm",
    albumDescription: "Deskripsyon",
    albumCoverOptional: "Imaj kouvèti (opsyonèl)",
    photosCount: "foto",
    downloadPhoto: "Telechaje foto a",
    downloadFailed: "Telechajman echwe. Eseye ankò.",
    voiceCall: "Rele",
    voiceDirectCall: "Apèl odyo dirèk",
    voiceDeptRoom: "Sal vokal depatman",
    voiceConnecting: "K ap konekte…",
    voiceRinging: "K ap sonnen…",
    voiceSpeaking: "K ap pale",
    voiceInCall: "Nan apèl la",
    voiceCallEnded: "Lòt moun nan fini apèl la",
    voiceNoAnswer: "Pa gen repons",
    broadcastEmpty: "Tape yon mesaj pou voye bay tout moun",
    broadcastSent: "Mesaj la voye",
    voiceRoomWaiting: "Se ou sèl ki la a. N ap tann lòt manm…",
    voiceInRoom: "nan sal la",
    voiceYou: "ou menm",
    voiceMute: "Fèmen mikwo",
    voiceUnmute: "Louvri mikwo",
    voiceHangup: "Fèmen apèl la",
    voiceEnableAudio: "Aktive son",
    voiceStartError: "Pa ka kòmanse apèl la. Eseye ankò.",
    voiceCalls: "Apèl vokal",
    voiceDirectFlag: "Vokal · apèl 1 pou 1",
    voiceDeptFlag: "Vokal · sal depatman",
    voiceCallDisabledColumn: "Rele",
    voiceCallDisabledTitle: "Ka kòmanse apèl",
    voiceRoomDisabledTitle: "Sal vokal",
    voiceMuteParticipant: "Fèmen mikwo",
    voiceUnmuteParticipant: "Louvri mikwo",
    voiceMuteFailed: "Pa ka fèmen mikwo. Eseye ankò.",
    voiceStartCall: "Kòmanse apèl",
    voiceJoinCall: "Antre nan apèl",
    voiceEndForAll: "Fini pou tout",
    voiceEndForAllConfirm: "Fini apèl la pou tout moun ki nan sal la?",
    voiceEnded: "Apèl la fini pou tout moun",
    voiceInvite: "Envite",
    voiceInvitePickerTitle: "Envite yon manm nan apèl la",
    broadcastLive: "Difizyon dirèk",
    broadcastStart: "Kòmanse difizyon dirèk",
    broadcastEnd: "Fini difizyon",
    broadcastEndConfirm: "Fini difizyon a pou tout moun?",
    broadcastListenNow: "Peze pou koute",
    broadcastListening: "W ap koute",
    broadcastListeningNow: "ap koute",
    broadcastListenOnlyHint: "Ou tande moun k ap pale a men w pa ka pale.",
    broadcastFromLower: "de",
    broadcastEnded: "Difizyon fini",
    voiceInviteSent: "Envitasyon voye",
    voiceVolume: "Vòlim",
    adminTileOrder: "Ranje griy",
    adminTileOrderDesc: "Chanje lòd tuile yo sou griy piblik, manm, ak admin.",
    adminTileOrderIntro: "Deplase tuile yo monte oswa desann pou chak griy. Nouvo bagay ki poko trie rete nan pozisyon lè default nan fen.",
    audienceAdmin: "Panèl admin",
    gridOrderMoveUp: "Monte",
    gridOrderMoveDown: "Desann",
    gridOrderSave: "Sove lòd la",
    gridOrderNothingToSave: "Anyen pou sove",
    gridOrderSaved: "Lòd sove",
    voiceIncomingCall: "Apèl k ap antre",
    voiceUnknownCaller: "Yon manm",
    voiceRingingSubtitle: "Odyo dirèk · tape pou reponn",
    voiceAnswer: "Reponn",
    voiceDecline: "Refize",
    uploadPhoto: "Ajoute foto",
    uploadingPhoto: "Ap ajoute…",
    pickAlbum: "Chwazi yon albòm",
    photoCaption: "Legend (opsyonèl)",
    awaitingApproval: "Ap tann apwobasyon admin",
    permissionRequired: "Ajoute pa otorize",
    permissionRequiredMsg: "Mande admin prensipal pou pèmisyon ajoute foto.",
    moderationQueue: "Ap tann apwobasyon",
    noPendingUploads: "Pa gen anyen ki ap tann.",
    approve: "Apwouve",
    reject: "Rejte",
    canUploadGallery: "Kapab ajoute foto",
    canUploadGalleryHint: "Bay manm sa a pèmisyon pou pwopoze foto nan galri (admin toujou apwouve chak).",
    featureVisibility: "Vizibilite fonksyonalite",
    featureVisibilityIntro: "Montre oswa kache nenpòt paj sou paj piblik la ak pou manm ki konekte yo. Sa ki pa la vizib pa default — nouvo fonksyonalite yo aktive otomatikman.",
    audiencePublic: "Piblik",
    audienceMembers: "Manm",
    visibleFeatures: "fonksyonalite vizib",
    showAll: "Montre tout",
    hideAll: "Kache tout",
    feature: "Fonksyonalite",
    publicShort: "Piblik",
    membersShort: "Manm",
    saveVisibility: "Anrejistre vizibilite",
    featuresSaved: "Vizibilite anrejistre · aktive",
    saveFailed: "Echwe pou anrejistre",
    saving: "N ap anrejistre…",
    adminDescFeatures: "Montre / kache paj",
    boardTierElders: "Ansyen",
    boardTierSecretary: "Sekretè",
    boardTierTreasurer: "Trezorye",
    boardTierAdministrators: "Administratè",
    boardTierDepartmentAdmins: "Administratè Depatman",
    autoFromDepartment: "Ajoute otomatikman soti nan depatman",
    boardAutoDeptHint: "Nenpòt moun ki tabli kòm administratè yon depatman (Admin → Depatman) ap ajoute otomatikman nan Konsèy la. Jere plas sa yo nan paj Depatman yo.",
    auto: "Oto",
    weeklySchedule: "Orè chak semèn",
    getInTouch: "Kontakte nou",
    sabbathLessons: "Leson Saba",
    sabbathLessonsDesc: "Leson chak semèn",
    ssIntroduction: "Entwodiksyon",
    ssReadIntro: "Li entwodiksyon trimès la",
    ssLessons: "Leson chak semèn",
    ssDay: "Jou",
    ssBackToLessons: "Retounen nan leson yo",
    ssBackToLesson: "Retounen nan leson an",
    ssNextDay: "Pwochen",
    ssPrevDay: "Anvan",
    ssCredits: "Kredi",
    ssHtFallback: "Tradiksyon Kreyòl la poko pibliye pou trimès sa a — n ap montre edisyon franse a.",

    // Admin & member UI (v2)
    roleMainAdmin: "Administratè prensipal",
    roleTreasurer: "Trezorye",
    roleElder: "Ansyen",
    roleFirstElder: "Premye ansyen",
    rolePastor: "Pastè",
    roleDeptAdmin: "Admin depatman",
    roleChurchSecretary: "Sekretè legliz la",
    roleMember: "Manm",
    financeDashboard: "Tablo finansye",
    financeDashboardSub: "Dim, ofrand ak analiz",
    openAdminPanel: "Ouvri panèl admin",
    openAdminPanelSub: "Jere kontni, manm, ak brand",
    adminPanel: "Panèl admin",
    manageChurch: "Jere legliz la",
    churchCode: "Kòd legliz",
    superAdmin: "Super admin",
    memberSignIn: "Konekte kòm manm",
    adminSignIn: "Konekte kòm admin",
    memberSignInHelp: "Konekte ak imèl ou ak modpas ou.",
    adminSignInHelp: "Jere legliz ou — konekte kòm admin.",
    superAdminHelp: "Jere tout legliz nan platfòm nan.",
    modeMember: "Mwen se yon manm",
    modeAdmin: "Mwen se yon admin",
    welcomeBack: "Byenveni",
    invalidCredentials: "Enfòmasyon konekte pa valab",
    newMember: "Nouvo manm?",
    createAccount: "Kreye yon kont",
    noChurchYet: "Pa gen legliz toujou?",
    registerChurch: "Anrejistre yon legliz",
    departmentsCount: "Depatman",
    actionRequired: "Aksyon nesesè",
    activateSubscription: "Aktive yon abònman pou manm ou yo ka itilize aplikasyon an.",
    adminDescAnnouncements: "Kreye ak pwograme piblikasyon",
    adminDescEvents: "Kalandriye ak RSVP",
    adminDescSermons: "Voye oswa lyen odyo/videyo",
    adminDescSongbook: "Ajoute chan ak kantik",
    adminDescBoard: "Ansyen, admin, dyak",
    adminDescPastor: "Bio, foto, mesaj",
    adminDescFirstElder: "Bio, foto, mesaj",
    adminDescDepartments: "Ministè ak sub-admin",
    adminDescFinance: "Dim, ofrand ak analiz",
    adminDescMembers: "Bay wòl admin",
    adminDescBranding: "Non, koulè, kouvèti",
    adminDescSubscription: "Plan ak fakti",
    adminTileMembers: "Manm ak wòl",
    add: "Ajoute",
    remove: "Retire",
    saving: "N ap sove…",
    saveChanges: "Sove chanjman yo",
    submitPost: "Pibliye",
    create: "Kreye",
    creating: "N ap ajoute…",
    deleteConfirm: "Efase?",
    done: "Fini",
    copy: "Kopye",
    copied: "Kopye",
    membersAndProfiles: "Manm ak pwofil",
    searchMembers: "Chèche pa non oswa imèl",
    membersEditHint: "Tape sou yon manm pou chanje non li, foto, bio, wòl ak depatman. Tape Ajoute pou kreye yon nouvo manm.",
    addNewMember: "Ajoute yon nouvo manm",
    editMemberProfile: "Modifye pwofil manm",
    memberEmailLabel: "Imèl",
    memberBirthDate: "Dat nesans",
    memberBio: "Bio",
    memberPhoto: "Foto",
    memberRole: "Wòl",
    memberDepartment: "Depatman",
    memberDepartments: "Depatman yo",
    memberDeptNone: "Okenn",
    memberProfileUpdated: "Pwofil modifye",
    memberAdded: "Manm ajoute",
    tempPasswordOptional: "Modpas tanporè (opsyonèl)",
    tempPasswordLeaveBlank: "Kite vid pou jenere otomatikman",
    memberCreated: "Manm kreye",
    memberCreatedHint: "Pataje idantifyan sa yo ak nouvo manm nan. Modpas la ap montre sèlman yon fwa — kopye li kounye a.",
    copyCredentials: "Kopye idantifyan yo",
    addMemberEssentials: "Ranpli enfòmasyon esansyèl yo. Kite modpas la vid pou jenere yon sa tanporè.",
    fullNameRequired: "Non konplè *",
    emailRequired: "Imèl *",
    emailOptional: "Imèl (opsyonèl)",
    importMembersCsv: "Enpòte manm ak yon fichye CSV",
    csvHelp: "Chaje yon fichye CSV ki soti nan Excel oswa Google Sheets. Sèl kolòn 'name' obligatwa. Ranje ki gen imèl deja itilize yo pral pase.",
    csvColumns: "Kolòn nou espere",
    csvDownloadTemplate: "Telechaje yon modèl",
    csvChooseFile: "Chwazi yon fichye CSV",
    csvImport: "Enpòte manm yo",
    csvPickFile: "Tanpri chwazi yon fichye CSV",
    csvNothingCreated: "Pa gen manm ki kreye — gade erè yo",
    csvImportFailed: "Enpòte a echwe",
    csvCreated: "Kreye",
    csvSkipped: "Sote",
    csvErrors: "Detay",
    membersImported: "manm enpòte",
    importing: "Enpòte…",
    select: "Chwazi",
    selectAll: "Chwazi tout",
    selected: "chwazi",
    clearSelection: "Efase",
    deleteSelected: "Efase",
    deleting: "Efase…",
    deleteMembersTitle: "Efase manm sa yo?",
    deleteMembersConfirm: "W ap efase {n} manm nan legliz ou nèt.",
    deleteMembersWarning: "Aksyon sa a pa ka anile. Ou pa ka efase pwòp tèt ou, epi dènye gwo administratè a toujou pwoteje.",
    membersDeleted: "manm efase",
    deleteFailed: "Efase echwe",
    newAnnouncement: "Nouvo anons",
    editAnnouncement: "Modifye anons",
    announcementTitle: "Tit",
    announcementBody: "Kò",
    coverImageOptional: "Imaj kouvèti (opsyonèl)",
    targetDepartmentOptional: "Depatman sib (opsyonèl)",
    allMembers: "Tout manm",
    pinToTop: "Kore sou tèt",
    pin: "Kore",
    unpin: "Retire kore",
    deptPost: "Post depatman",
    publicPost: "Piblik",
    noAnnouncementsYet: "Pa gen anons.",
    updated: "Modifye",
    posted: "Pibliye",
    newEvent: "Nouvo evènman",
    editEvent: "Modifye evènman",
    eventTitle: "Tit",
    eventDescription: "Deskripsyon",
    eventLocation: "Kote",
    eventStartsAt: "Kòmanse",
    eventCreated: "Evènman kreye",
    eventUpdated: "Evènman modifye",
    deleteEventConfirm: "Efase evènman?",
    noEventsYet: "Pa gen evènman.",
    going: "prale",
    maybe: "petèt",
    cantAttend: "pa ka",
    newSermon: "Nouvo sèmon",
    editSermon: "Modifye sèmon",
    addSermon: "Ajoute sèmon",
    speaker: "Predikatè",
    series: "Seri",
    preachedOn: "Preche",
    durationMin: "Dire (min)",
    scripture: "Ekriti",
    mediaKind: "Tip medya",
    kindYoutube: "ID YouTube",
    kindVideoUrl: "URL videyo (.mp4)",
    kindAudioUrl: "URL odyo (.mp3)",
    kindAudioFile: "Fichye odyo",
    kindVideoFile: "Fichye videyo",
    youtubeIdLabel: "ID videyo YouTube",
    directUrl: "URL dirèk",
    audioFile: "Fichye odyo (.mp3)",
    videoFile: "Fichye videyo (.mp4)",
    chooseFile: "Chwazi fichye (max 200 MB)",
    uploading: "Voye…",
    fileAttached: "✓ Fichye atache",
    thumbnailOptional: "Vignèt (opsyonèl)",
    sermonAdded: "Sèmon ajoute",
    sermonUpdated: "Sèmon modifye",
    deleteSermonConfirm: "Efase sèmon?",
    noSermonsYet: "Pa gen sèmon.",
    addMediaFirst: "Ajoute yon lyen oswa yon fichye",
    churchBoard: "Konsèy legliz",
    addBoardMember: "Ajoute manm konsèy",
    addToBoard: "Ajoute nan konsèy",
    editBoardSeat: "Modifye pòs konsèy",
    boardMemberSelect: "Chwazi manm",
    boardTier: "Nivo",
    boardRoleTitle: "Wòl",
    boardOrder: "Lòd",
    noBoardYet: "Pa gen manm konsèy.",
    submitPrayerCta: "Voye yon demand priyè",
    submitPrayerHint: "Konekte pou pataje yon demand",
    loadingPrayerWall: "Chajman mi priyè…",
    noPrayersYet: "Pa gen demand priyè.",
    prayerLabel: "Priyè",
    prayingCount: "ap priye",
    carpoolCta: "Konekte pou ofri yon vwayaj",
    carpoolCtaHint: "Enfòmasyon kontak yo pataje sèlman ak manm ki konekte",
    postRide: "Ofri oswa mande yon vwayaj",
    loadingCarpool: "Chajman kovwaturaj…",
    noCarpoolYet: "Pa gen vwayaj disponib.",
    rideOffers: "Vwayaj yo ofri",
    rideRequests: "Demand vwayaj",
    offeringRide: "Ofri yon vwayaj",
    needsRide: "Bezwen yon vwayaj",
    seat: "plas",
    seats: "plas",
    yearbookSearch: "Chèche pa non oswa depatman…",
    loadingYearbook: "Chajman liv manm…",
    yearbookNoMatch: "Pa gen manm ki matche.",
    yearbookNoListed: "Pa gen manm ki pibliye.",
    communityPrayerRequests: "Demand priyè kominotè",
    shareRidesToService: "Pataje vwayaj pou ale nan sèvis",
    churchFamilyDirectory: "Anyè fanmi legliz la",
    prayerWall: "Demand priyè",
    admin: "Admin",
    public: "Piblik",
    watch: "Gade",
    listen: "Koute",
  },

  es: {
    appName: "Iglesia Adventista Haitiana de Betania",
    appNameShort: "Betania Haitiana ASD",

    chooseChurch: "Elige tu iglesia",
    chooseChurchSub: "Selecciona tu comunidad para iniciar sesión",
    signIn: "Iniciar sesión",
    signInShort: "Entrar",
    signOut: "Salir",
    register: "Crear cuenta",
    email: "Correo",
    password: "Contraseña",
    name: "Nombre completo",
    phone: "Teléfono (opcional)",
    welcome: "Bienvenido",
    guest: "invitado",
    welcomeBack: "Bienvenido de nuevo",
    welcomeTo: "Bienvenido a",
    memberOrAdmin: "Miembro o admin",
    memberOrAdminDesc: "Ingresa tu correo y contraseña",
    registerNewChurch: "Registrar una nueva iglesia",
    registerNewChurchDesc: "Solicitud de aprobación — normalmente en 24 horas",
    superAdminAccess: "Acceso Super Admin",
    signInHint: "Elige cómo quieres acceder a la app.",
    memberSignIn: "Ingreso miembro",
    memberSignInSub: "RSVP, muro de oración, historial de ofrendas y más",

    home: "Inicio",
    bible: "Biblia",
    giving: "Ofrendas",
    songbook: "Cantos",
    more: "Más",

    announcements: "Anuncios",
    events: "Eventos",
    sermons: "Sermones",
    yearbook: "Álbum",
    board: "Junta",
    pastor: "Pastor",
    firstElder: "Primer Anciano",
    elders: "Ancianos",
    departments: "Departamentos",
    live: "En vivo",
    prayers: "Petición de oración",
    carpool: "Transporte",
    sabbathSet: "Programa del Sábado",
    sabbathSetHeading: "Programa del Sábado",
    sabbathSetSubtitle: "Himnos, lecturas y orden del culto para este Sábado.",
    sabbathSetToday: "Programa de hoy",
    sabbathSetUpcoming: "Próximos Sábados",
    sabbathSetNone: "Aún no se ha compartido un programa.",
    sabbathSetShareCTA: "Abrir el programa",
    sabbathSetItemHymn: "Himno",
    sabbathSetItemScripture: "Lectura bíblica",
    sabbathSetItemText: "Texto / nota",
    sabbathSetItemPrayer: "Oración",
    sabbathSetItemWelcome: "Bienvenida y saludos",
    sabbathSetItemOffering: "Diezmos y ofrendas",
    sabbathSetItemDoxology: "Doxología / Bendición",
    sabbathSetItemSpecialMusic: "Música especial",
    sabbathSetItemTestimony: "Testimonio",
    sabbathSetItemChildrenStory: "Historia infantil",
    sabbathSetItemResponsiveReading: "Lectura responsiva",
    sabbathSetItemSermon: "Sermón",
    sabbathSetItemAnnouncement: "Anuncios",
    sabbathSetItemSlides: "Diapositivas / Presentación",
    sabbathSetOpenSlides: "Abrir diapositivas",
    sabbathSetItemPerson: "Persona / líder (opcional)",
    roleWorshipLeader: "Líder de alabanza",
    messages: "Llamadas y mensajes",
    members: "Miembros",
    memberProfile: "Perfil del miembro",
    membersSearchPlaceholder: "Buscar miembros…",
    noMembersFound: "No se encontraron miembros.",
    memberNotFound: "Miembro no encontrado.",
    sendMessage: "Enviar mensaje",
    startNewChat: "Nueva conversación",
    browseMembersToDm: "Explora miembros y empieza una conversación",
    chatEmptyState: "Aún no hay conversaciones. Escribe a un miembro para empezar.",
    offlineBanner: "Sin conexión — mostrando contenido guardado.",
    conversations: "Conversaciones",
    yourDepartments: "Tus departamentos",
    openChat: "Abrir chat",
    tapToJoinChat: "Toca para unirte al grupo",
    noMessagesYet: "Aún no hay mensajes.",
    messagePlaceholder: "Mensaje…",
    messageSendFailed: "No se pudo enviar el mensaje.",
    uploadFailed: "Error al subir.",
    attachImage: "Adjuntar imagen",
    muteSound: "Silenciar sonido de notificaciones",
    unmuteSound: "Activar sonido de notificaciones",
    registerChurchByInviteOnly: "Las nuevas iglesias se registran solo por invitación. Contacta al administrador de Church Hub para comenzar.",
    isTyping: "está escribiendo…",
    seenBy: "Visto por",
    membersLower: "miembros",
    member: "Miembro",
    chatNotFound: "Este chat no está disponible.",
    backToChats: "Volver a los chats",
    all: "Todos",
    justNow: "ahora",
    youPrefix: "Tú",
    back: "Atrás",
    notifications: "Notificaciones",
    settings: "Ajustes",
    language: "Idioma",
    birthdayWishTemplate: "¡Feliz cumpleaños, {name}! 🎂 Te deseo un año lleno de alegría y las más ricas bendiciones del Señor. 🎉",
    birthdayLabel: "Cumpleaños",
    birthdays: "Muro de cumpleaños",
    liveStartPush: "Aviso de inicio del servicio en vivo",
    birthdayCelebrantThankYou: "Gracias",
    birthdayCelebrantAppreciate: "Los aprecio",
    birthdayCelebrantHelp: "Un gracias rápido — toca uno:",
    birthdayWallEmpty: "Nadie cumple años hoy. ¡Vuelve mañana!",
    notifDeleteSelected: "Eliminar selección",
    notifDeleteAll: "Eliminar todo",
    notifSelectAll: "Seleccionar todo",
    notifDeleteConfirm: "¿Eliminar {count} notificación(es)?",
    notifDeleteAllConfirm: "¿Eliminar todo? Esta acción es irreversible.",
    notifDeletedToast: "Eliminado",
    wishDeleteConfirm: "¿Eliminar este mensaje?",
    wishDeletedToast: "Mensaje eliminado",
    registerHeading: "Crea tu cuenta de iglesia",
    registerSubtitle: "Unos datos para ubicarte en la lista de miembros.",
    finance: "Finanzas",
    membersAndRoles: "Miembros y roles",
    branding: "Identidad",
    subscription: "Suscripción",

    descAnnouncements: "Últimas noticias",
    descEvents: "Calendario próximo",
    descSermons: "Mensajes recientes",
    descSongbook: "Himnos y cantos",
    descBoard: "Ancianos, admins, diáconos",
    descPastor: "Conoce a nuestro pastor",
    descFirstElder: "Conoce a nuestro primer anciano",
    descDepartments: "Ministerios y grupos",
    descGiving: "Diezmos, ofrendas y donaciones",
    descLive: "Ver el culto en vivo",

    heroSubtitle:
      "Una comunidad Adventista Haitiana — adoración, comunión y servicio. Bienvenido a casa.",
    sabbathLine1: "Culto",
    sabbathLine2: "de sábado",
    langChipHint: "Kreyòl · FR · EN",
    libraryChipHint: "Biblia · Himnos · Vivo",
    exploreEyebrow: "Explorar",
    browseOurChurch: "Conoce nuestra iglesia",
    noLoginRequired: "Sin iniciar sesión",
    signInForMore:
      "Inicia sesión para RSVP, oración, notas personales de Biblia e historial de ofrendas.",
    chooseLanguage: "Elige tu idioma",
    openLibrary: "Abrir la biblioteca",
    hymnalsLabel: "Himnos",
    hymnalsDesc: "Chan Desperans y más",

    submit: "Enviar",
    cancel: "Cancelar",
    chatSwipeHint: "Mantén presionado o desliza a la izquierda para eliminar",
    chatNSelected: "{n} seleccionadas",
    chatDeleteOne: "Conversación eliminada",
    chatDeleteMany: "{n} conversaciones eliminadas",
    chatDeleteError: "No se pudo eliminar esta conversación",
    deleteConversationTitle: "¿Eliminar esta conversación?",
    deleteNConversationsTitle: "¿Eliminar {n} conversaciones?",
    deleteForMe: "Eliminar para mí",
    deleteForMeHint: "Se quita de tu bandeja. Un nuevo mensaje la traerá de vuelta.",
    deleteForEveryone: "Eliminar para todos",
    deleteForEveryoneHint: "Elimina el hilo y todos los mensajes para cada participante.",
    deleteForEveryoneUnavailable: "No tienes permiso.",
    save: "Guardar",
    close: "Cerrar",
    back: "Atrás",
    next: "Siguiente",
    previous: "Anterior",
    loading: "Cargando…",
    search: "Buscar",
    edit: "Editar",
    delete: "Eliminar",
    confirm: "Confirmar",
    yes: "Sí",
    no: "No",
    optional: "(opcional)",
    all: "Todos",
    tapToRead: "Toca para leer",

    todayVerse: "Versículo del día",
    pinned: "Fijado",
    pinnedAnnouncements: "Fijados",
    quickActions: "Acciones rápidas",
    upcomingServices: "Próximos servicios",

    noAnnouncements: "Aún no hay anuncios.",
    announcement: "Anuncio",

    upcoming: "Próximos",
    upcomingTab: "Próximos",
    allTab: "Todos",
    locationTBA: "Ubicación por confirmar",
    goingLabel: "van",
    maybeLabel: "tal vez",
    noEvents: "Aún no hay eventos.",
    signInToRsvp: "Inicia sesión para confirmar asistencia.",

    guestSpeaker: "Predicador invitado",
    noSermons: "Aún no hay sermones.",
    signInToPlay: "Inicia sesión para reproducir.",
    searchSermons: "Buscar sermones…",

    songSearch: "Buscar título, número o letra…",
    songSearchShort: "Título, número, letra…",
    allHymnals: "Todos los himnarios",
    hymnal: "Himnario",
    noSongs: "No se encontraron cantos.",
    noLyrics: "Letra no disponible.",

    bibleSearch: "Buscar versículos…",
    bibleSearchLong: "Buscar versículo o referencia…",
    bibleSearchPlaceholder: "Prueba «Juan 3:16», «Sal 23», o texto libre…",
    bibleSearchTip: "Consejo: ingresa una referencia como",
    bibleSearchTipExample: "para ir directo.",
    bibleNoteToSave: "Inicia sesión para guardar notas personales.",
    bibleNote: "Se cargan más capítulos según se necesiten.",
    noSearchResults: "Sin resultados. Prueba «Juan 3:16» o «Sal 23».",
    searchFailed: "La búsqueda falló.",
    openingRef: "Abriendo",

    churchLeadership: "Liderazgo de la iglesia",
    elders: "Ancianos",
    deacons: "Diáconos",
    administrators: "Administradores",
    noBoard: "Los miembros de la junta aparecerán aquí.",
    noPastor: "El perfil del pastor aún no se ha añadido.",
    noFirstElder: "El perfil del primer anciano aún no se ha añadido.",
    seniorPastor: "Pastor principal",
    favoriteVerse: "Versículo favorito",
    about: "Acerca de",
    submitPrayerRequest: "Enviar una petición de oración",
    sentPrivatelyPastor: "Se envía en privado al pastor.",
    prayerBodyLabel: "Oración",
    prayerSentToPastor: "Tu petición de oración fue enviada al pastor.",
    prayerSendFailed: "No se pudo enviar la petición de oración.",
    yourNameOptional: "Tu nombre (opcional)",
    yourNamePlaceholder: "Marie Joseph",
    aboutSection: "Acerca de",
    noBio: "No se ha añadido una biografía.",
    pastorsMessage: "Mensaje del pastor",
    ministryDepartment: "Ministerio / departamento",
    memberSince: "Miembro desde",
    appRole: "Rol en la app",
    noDepartments: "Aún no hay departamentos.",
    memberCountSuffix: "miembro",
    membersCountSuffix: "miembros",

    liveTitleFallback: "Culto en vivo",
    liveNotConfigured: "La transmisión en vivo aún no está configurada.",
    openChannel: "Abrir canal de YouTube",
    serviceSchedule: "Horario de cultos",
    recentServices: "Cultos recientes",
    watchOnYouTube: "Ver en YouTube",
    noLiveContent: "No hay contenido en vivo aún.",

    giftMakesDifference: "Cada ofrenda cuenta",
    givingIntro: "Pago seguro por Stripe. Sin cuenta necesaria — recibirás un recibo por correo.",
    yourInfoOptional: "Tus datos (opcional)",
    yourName: "Tu nombre",
    emailForReceipt: "Correo (para recibo)",
    donorPrivacyNote:
      "Déjalo vacío para dar anónimamente. Stripe enviará un recibo si ingresas un correo al pagar.",
    tithesAndOfferings: "Diezmos y ofrendas",
    donation: "Donación",
    enterAmountHint: "Ingresa un monto para diezmo, ofrenda o ambos.",
    tithe: "Diezmo",
    offering: "Ofrenda",
    totalToGive: "Total a dar",
    noteOptional: "Nota (opcional)",
    notePlaceholder: "En memoria de, dedicación…",
    payTithe: "Dar diezmo",
    payOffering: "Dar ofrenda",
    custom: "Monto personalizado",
    continueToPayment: "Continuar al pago seguro",
    givePlaceholder: "Ingresa un monto",
    givingHistoryNote: "Inicia sesión para ver tu historial y recibos.",
    yourTransactions: "Tu historial",
    fund: "Fondo",
    generalDonation: "Donación general",
    missions: "Misiones",
    buildingFund: "Fondo de construcción",
    amountUsd: "Monto (USD)",
    customAmount: "Monto personalizado",
    supportSpecificFunds: "Apoya fondos específicos además de tu diezmo regular.",
    redirecting: "Redirigiendo…",
    couldNotStartPayment: "No se pudo iniciar el pago.",
    enterAmountToast: "Ingresa un monto para diezmo, ofrenda o ambos.",

    paymentSuccess: "¡Gracias por tu donativo!",
    paymentPending: "Confirmando pago…",
    paymentFailed: "El pago no se completó",
    backToHome: "Volver al inicio",
    tryAgain: "Intentar de nuevo",

    askToPray: "Oré",
    anonymous: "Anónimo",
    forPastor: "Solo al pastor",
    prayerTitlePlaceholder: "Título (opcional)",
    prayerBodyPlaceholder: "Comparte tu petición…",
    submitPrayer: "Enviar oración",

    youth: "Jóvenes",
    music: "Música",
    elderly: "Mayores",
    children: "Niños",

    nowLive: "En vivo ahora",
    liveNow: "EN VIVO",
    notLive: "No hay transmisión. Repetición disponible.",

    noResults: "Sin resultados",
    signInSuffix: "Inicia sesión para acceder.",
    moreDialogTitle: "Más",
    aboutUs: "Sobre nosotros",
    contactDeveloper: "Contactar al desarrollador",
    aboutTheDeveloper: "Sobre el desarrollador",
    aboutTheDeveloperEyebrow: "Sobre el desarrollador",
    aboutTheDeveloperDesc: "Conoce a la persona detrás de la app.",
    contactDeveloperTitle: "Hecho para tu iglesia",
    contactDeveloperSubtitle: "Ideas, errores o un saludo — el desarrollador lee cada mensaje.",
    contactDeveloperPromise: "Las respuestas llegan en menos de 48 horas.",
    contactDeveloperSuccess: "Mensaje enviado — ¡gracias!",
    contactDeveloperError: "Algo salió mal. Inténtalo de nuevo en un momento.",
    contactDeveloperRateLimited: "Enviaste varios mensajes recientemente — vuelve a intentarlo en una hora.",
    contactDeveloperThankYou: "¡Mensaje en camino!",
    contactDeveloperThankYouBody: "Tu mensaje llegó al desarrollador. Recibirás respuesta en menos de 48 horas.",
    contactDeveloperSendAnother: "Enviar otro",
    contactDeveloperSubjectPlaceholder: "ej. Idea: recordatorios de cumpleaños por email",
    contactDeveloperMessagePlaceholder: "Comparte todo el detalle que puedas — puedes adjuntar capturas por respuesta.",
    aboutTheApp: "Acerca de la app",
    yourName: "Tu nombre",
    yourEmail: "Correo para responder",
    subject: "Asunto",
    message: "Mensaje",
    sending: "Enviando…",
    aboutUsDesc: "Nuestra historia, misión y pastor",
    visitInPerson: "Visítanos en persona",
    visitInPersonDesc: "Dirección y horarios de servicio",
    aboutOurChurch: "Sobre nuestra iglesia",
    ourPastor: "Nuestro pastor",
    openInMaps: "Abrir en Google Maps",
    noAddressYet: "Aún no hay dirección — vuelve pronto.",
    gallery: "Fotos de la iglesia",
    galleryDesc: "Momentos de nuestros servicios y eventos",
    galleryStripCta: "Ver la galería",
    galleryEmptyStrip: "Aún no hay fotos — vuelve pronto.",
    albums: "Álbumes",
    noAlbumsYet: "No hay álbumes todavía.",
    noPhotosYet: "No hay fotos en este álbum.",
    createAlbum: "Crear álbum",
    editAlbum: "Editar álbum",
    newAlbum: "Nuevo álbum",
    albumName: "Nombre del álbum",
    albumDescription: "Descripción",
    albumCoverOptional: "Portada (opcional)",
    photosCount: "fotos",
    downloadPhoto: "Descargar foto",
    downloadFailed: "Descarga fallida. Inténtalo de nuevo.",
    voiceCall: "Llamar",
    voiceDirectCall: "Llamada de audio en vivo",
    voiceDeptRoom: "Sala de voz del departamento",
    voiceConnecting: "Conectando…",
    voiceRinging: "Llamando…",
    voiceSpeaking: "Hablando",
    voiceInCall: "En llamada",
    voiceCallEnded: "La otra persona finalizó la llamada",
    voiceNoAnswer: "Sin respuesta",
    broadcastEmpty: "Escribe un mensaje para difundir",
    broadcastSent: "Difusión enviada",
    voiceRoomWaiting: "Estás solo aquí. Esperando a los demás…",
    voiceInRoom: "en la sala",
    voiceYou: "tú",
    voiceMute: "Silenciar",
    voiceUnmute: "Activar micrófono",
    voiceHangup: "Colgar",
    voiceEnableAudio: "Activar audio",
    voiceStartError: "No se pudo iniciar la llamada. Inténtalo de nuevo.",
    voiceCalls: "Llamadas de voz",
    voiceDirectFlag: "Voz · llamadas 1 a 1",
    voiceDeptFlag: "Voz · salas de departamento",
    voiceCallDisabledColumn: "Llamar",
    voiceCallDisabledTitle: "Puede iniciar llamadas",
    voiceRoomDisabledTitle: "Sala de voz",
    voiceMuteParticipant: "Silenciar participante",
    voiceUnmuteParticipant: "Activar micrófono",
    voiceMuteFailed: "No se pudo silenciar. Inténtalo de nuevo.",
    voiceStartCall: "Iniciar llamada",
    voiceJoinCall: "Unirse a la llamada",
    voiceEndForAll: "Terminar para todos",
    voiceEndForAllConfirm: "¿Terminar la llamada para todos los participantes?",
    voiceEnded: "Llamada terminada para todos",
    voiceInvite: "Invitar",
    voiceInvitePickerTitle: "Invitar a un miembro",
    broadcastLive: "Transmisión en vivo",
    broadcastStart: "Iniciar transmisión en vivo",
    broadcastEnd: "Terminar transmisión",
    broadcastEndConfirm: "¿Terminar la transmisión para todos?",
    broadcastListenNow: "Toca para escuchar",
    broadcastListening: "Estás escuchando",
    broadcastListeningNow: "escuchando",
    broadcastListenOnlyHint: "Puedes escuchar al orador pero no hablar.",
    broadcastFromLower: "de",
    broadcastEnded: "Transmisión terminada",
    voiceInviteSent: "Invitación enviada",
    voiceVolume: "Volumen",
    adminTileOrder: "Disposición de la cuadrícula",
    adminTileOrderDesc: "Reordena las tarjetas en las cuadrículas pública, miembros y admin.",
    adminTileOrderIntro: "Mueve las tarjetas arriba o abajo en cada cuadrícula. Lo que no ordenes mantiene su posición al final.",
    audienceAdmin: "Panel de admin",
    gridOrderMoveUp: "Subir",
    gridOrderMoveDown: "Bajar",
    gridOrderSave: "Guardar orden",
    gridOrderNothingToSave: "Nada que guardar",
    gridOrderSaved: "Orden guardado",
    voiceIncomingCall: "Llamada entrante",
    voiceUnknownCaller: "Un miembro",
    voiceRingingSubtitle: "Audio en vivo · toca para contestar",
    voiceAnswer: "Contestar",
    voiceDecline: "Rechazar",
    uploadPhoto: "Subir foto",
    uploadingPhoto: "Subiendo…",
    pickAlbum: "Elige un álbum",
    photoCaption: "Título (opcional)",
    awaitingApproval: "Esperando aprobación del administrador",
    permissionRequired: "Subida no permitida",
    permissionRequiredMsg: "Pide al administrador permiso para subir fotos.",
    moderationQueue: "Pendiente de revisión",
    noPendingUploads: "No hay subidas pendientes.",
    approve: "Aprobar",
    reject: "Rechazar",
    canUploadGallery: "Puede subir fotos a la galería",
    canUploadGalleryHint: "Permite a este miembro proponer fotos para la galería (el administrador aprueba cada una).",
    featureVisibility: "Visibilidad de funciones",
    featureVisibilityIntro: "Muestra u oculta cualquier página en la página pública y para los miembros conectados. Lo que falta es visible por defecto — las funciones nuevas aparecen automáticamente.",
    audiencePublic: "Público",
    audienceMembers: "Miembros",
    visibleFeatures: "funciones visibles",
    showAll: "Mostrar todo",
    hideAll: "Ocultar todo",
    feature: "Función",
    publicShort: "Público",
    membersShort: "Miembros",
    saveVisibility: "Guardar visibilidad",
    featuresSaved: "Visibilidad guardada · en vivo",
    saveFailed: "Error al guardar",
    saving: "Guardando…",
    adminDescFeatures: "Mostrar / ocultar páginas",
    boardTierElders: "Ancianos",
    boardTierSecretary: "Secretario",
    boardTierTreasurer: "Tesorero",
    boardTierAdministrators: "Administradores",
    boardTierDepartmentAdmins: "Administradores de departamento",
    autoFromDepartment: "Añadido automáticamente desde el departamento",
    boardAutoDeptHint: "Cualquier persona designada como administrador de un departamento (Admin → Departamentos) se agrega automáticamente a la Junta. Gestiona esos puestos desde la página de Departamentos.",
    auto: "Auto",
    weeklySchedule: "Horario semanal",
    getInTouch: "Contáctanos",
    sabbathLessons: "Lecciones de Sábado",
    sabbathLessonsDesc: "Lecciones semanales",
    ssIntroduction: "Introducción",
    ssReadIntro: "Leer la introducción del trimestre",
    ssLessons: "Lecciones semanales",
    ssDay: "Día",
    ssBackToLessons: "Volver a las lecciones",
    ssBackToLesson: "Volver a la lección",
    ssNextDay: "Siguiente",
    ssPrevDay: "Anterior",
    ssCredits: "Créditos",
    ssHtFallback: "La traducción al criollo haitiano aún no está publicada para este trimestre.",

    // Admin & member UI (v2)
    roleMainAdmin: "Administrador principal",
    roleTreasurer: "Tesorero",
    roleElder: "Anciano",
    roleFirstElder: "Primer anciano",
    rolePastor: "Pastor",
    roleDeptAdmin: "Admin de dpto.",
    roleChurchSecretary: "Secretaria de la iglesia",
    roleMember: "Miembro",
    financeDashboard: "Panel de finanzas",
    financeDashboardSub: "Diezmos, ofrendas y análisis",
    openAdminPanel: "Abrir panel admin",
    openAdminPanelSub: "Gestionar contenido, miembros y marca",
    adminPanel: "Panel admin",
    manageChurch: "Gestionar iglesia",
    churchCode: "Código de iglesia",
    superAdmin: "Súper administrador",
    memberSignIn: "Inicio de sesión de miembro",
    adminSignIn: "Inicio de sesión de admin",
    memberSignInHelp: "Inicia sesión con tu correo y contraseña.",
    adminSignInHelp: "Administra tu iglesia — inicia sesión como admin.",
    superAdminHelp: "Administra todas las iglesias de la plataforma.",
    modeMember: "Soy miembro",
    modeAdmin: "Soy administrador",
    welcomeBack: "Bienvenido de nuevo",
    invalidCredentials: "Credenciales no válidas",
    newMember: "¿Nuevo miembro?",
    createAccount: "Crear una cuenta",
    noChurchYet: "¿Sin iglesia aún?",
    registerChurch: "Registrar una iglesia",
    departmentsCount: "Departamentos",
    actionRequired: "Acción requerida",
    activateSubscription: "Activa una suscripción para que los miembros usen la app.",
    adminDescAnnouncements: "Crear y programar publicaciones",
    adminDescEvents: "Calendario y RSVP",
    adminDescSermons: "Subir o enlazar audio/video",
    adminDescSongbook: "Añadir himnos y cánticos",
    adminDescBoard: "Ancianos, admins, diáconos",
    adminDescPastor: "Bio, foto, mensaje",
    adminDescFirstElder: "Bio, foto, mensaje",
    adminDescDepartments: "Ministerios y sub-admins",
    adminDescFinance: "Diezmos, ofrendas y análisis",
    adminDescMembers: "Asignar roles admin",
    adminDescBranding: "Nombre, colores, portada",
    adminDescSubscription: "Plan y facturación",
    adminTileMembers: "Miembros y roles",
    add: "Añadir",
    remove: "Quitar",
    saving: "Guardando…",
    saveChanges: "Guardar cambios",
    submitPost: "Publicar",
    create: "Crear",
    creating: "Añadiendo…",
    deleteConfirm: "¿Eliminar?",
    done: "Listo",
    copy: "Copiar",
    copied: "Copiado",
    membersAndProfiles: "Miembros y perfiles",
    searchMembers: "Buscar por nombre o correo",
    membersEditHint: "Toca a un miembro para editar su nombre, foto, bio, rol y departamento. Toca Añadir para crear un nuevo miembro con contraseña temporal.",
    addNewMember: "Añadir nuevo miembro",
    editMemberProfile: "Editar perfil de miembro",
    memberEmailLabel: "Correo",
    memberBirthDate: "Fecha de nacimiento",
    memberBio: "Biografía",
    memberPhoto: "Foto",
    memberRole: "Rol",
    memberDepartment: "Departamento",
    memberDepartments: "Departamentos",
    memberDeptNone: "Ninguno",
    memberProfileUpdated: "Perfil actualizado",
    memberAdded: "Miembro añadido",
    tempPasswordOptional: "Contraseña temporal (opcional)",
    tempPasswordLeaveBlank: "Déjala vacía para autogenerar",
    memberCreated: "Miembro creado",
    memberCreatedHint: "Comparte estas credenciales con el nuevo miembro. La contraseña se mostrará solo una vez — cópiala ahora.",
    copyCredentials: "Copiar credenciales",
    addMemberEssentials: "Rellena lo esencial. Deja la contraseña vacía para generarla.",
    fullNameRequired: "Nombre completo *",
    emailRequired: "Correo *",
    emailOptional: "Correo (opcional)",
    importMembersCsv: "Importar miembros desde CSV",
    csvHelp: "Sube un archivo CSV exportado desde Excel o Google Sheets. Solo la columna 'name' es obligatoria. Las filas con un correo duplicado se omiten.",
    csvColumns: "Columnas esperadas",
    csvDownloadTemplate: "Descargar plantilla",
    csvChooseFile: "Elegir un archivo CSV",
    csvImport: "Importar miembros",
    csvPickFile: "Elige un archivo CSV primero",
    csvNothingCreated: "No se creó ningún miembro — revisa los errores",
    csvImportFailed: "Error de importación",
    csvCreated: "Creados",
    csvSkipped: "Omitidos",
    csvErrors: "Detalles",
    membersImported: "miembros importados",
    importing: "Importando…",
    select: "Seleccionar",
    selectAll: "Seleccionar todos",
    selected: "seleccionado(s)",
    clearSelection: "Limpiar",
    deleteSelected: "Eliminar",
    deleting: "Eliminando…",
    deleteMembersTitle: "¿Eliminar estos miembros?",
    deleteMembersConfirm: "Estás por eliminar permanentemente {n} miembro(s) de tu iglesia.",
    deleteMembersWarning: "Esta acción no se puede deshacer. No puedes eliminarte a ti mismo, y el último administrador principal siempre está protegido.",
    membersDeleted: "miembros eliminados",
    deleteFailed: "Error al eliminar",
    newAnnouncement: "Nuevo anuncio",
    editAnnouncement: "Editar anuncio",
    announcementTitle: "Título",
    announcementBody: "Contenido",
    coverImageOptional: "Imagen de portada (opcional)",
    targetDepartmentOptional: "Departamento destino (opcional)",
    allMembers: "Todos los miembros",
    pinToTop: "Fijar arriba",
    pin: "Fijar",
    unpin: "Desfijar",
    deptPost: "Post dpto.",
    publicPost: "Público",
    noAnnouncementsYet: "Sin anuncios.",
    updated: "Actualizado",
    posted: "Publicado",
    newEvent: "Nuevo evento",
    editEvent: "Editar evento",
    eventTitle: "Título",
    eventDescription: "Descripción",
    eventLocation: "Lugar",
    eventStartsAt: "Comienza",
    eventCreated: "Evento creado",
    eventUpdated: "Evento actualizado",
    deleteEventConfirm: "¿Eliminar evento?",
    noEventsYet: "Sin eventos.",
    going: "asistirán",
    maybe: "quizás",
    cantAttend: "no pueden",
    newSermon: "Nuevo sermón",
    editSermon: "Editar sermón",
    addSermon: "Añadir sermón",
    speaker: "Predicador",
    series: "Serie",
    preachedOn: "Predicado el",
    durationMin: "Duración (min)",
    scripture: "Pasaje",
    mediaKind: "Tipo de medio",
    kindYoutube: "ID de YouTube",
    kindVideoUrl: "URL de video (.mp4)",
    kindAudioUrl: "URL de audio (.mp3)",
    kindAudioFile: "Archivo de audio",
    kindVideoFile: "Archivo de video",
    youtubeIdLabel: "ID de YouTube (11 car.)",
    directUrl: "URL directa",
    audioFile: "Archivo de audio (.mp3)",
    videoFile: "Archivo de video (.mp4)",
    chooseFile: "Elegir archivo (máx 200 MB)",
    uploading: "Subiendo…",
    fileAttached: "✓ Archivo adjunto",
    thumbnailOptional: "Miniatura (opcional)",
    sermonAdded: "Sermón añadido",
    sermonUpdated: "Sermón actualizado",
    deleteSermonConfirm: "¿Eliminar sermón?",
    noSermonsYet: "Sin sermones.",
    addMediaFirst: "Añade un enlace o sube un archivo",
    churchBoard: "Junta de la iglesia",
    addBoardMember: "Añadir a la junta",
    addToBoard: "Añadir a la junta",
    editBoardSeat: "Editar puesto",
    boardMemberSelect: "Elegir miembro",
    boardTier: "Nivel",
    boardRoleTitle: "Cargo",
    boardOrder: "Orden",
    noBoardYet: "Sin miembros de la junta.",
    submitPrayerCta: "Enviar una petición de oración",
    submitPrayerHint: "Inicia sesión para compartir una petición",
    loadingPrayerWall: "Cargando muro de oración…",
    noPrayersYet: "Sin peticiones de oración.",
    prayerLabel: "Oración",
    prayingCount: "orando",
    carpoolCta: "Inicia sesión para ofrecer un viaje",
    carpoolCtaHint: "Los contactos solo se comparten con miembros conectados",
    postRide: "Ofrecer o solicitar un viaje",
    loadingCarpool: "Cargando carpool…",
    noCarpoolYet: "Sin viajes disponibles.",
    rideOffers: "Viajes ofrecidos",
    rideRequests: "Solicitudes de viaje",
    offeringRide: "Ofrece viaje",
    needsRide: "Necesita viaje",
    seat: "plaza",
    seats: "plazas",
    yearbookSearch: "Buscar por nombre o departamento…",
    loadingYearbook: "Cargando álbum…",
    yearbookNoMatch: "Ningún miembro coincide.",
    yearbookNoListed: "No hay miembros publicados aún.",
    communityPrayerRequests: "Peticiones de oración comunitarias",
    shareRidesToService: "Compartir viajes al culto",
    churchFamilyDirectory: "Directorio de la familia",
    prayerWall: "Petición de oración",
    admin: "Admin",
    public: "Público",
    watch: "Ver",
    listen: "Escuchar",
  },
};

const I18nCtx = createContext({ lang: "en", t: (k) => k, setLang: () => {} });

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(
    () => localStorage.getItem("ch_lang") || "en"
  );

  useEffect(() => {
    localStorage.setItem("ch_lang", lang);
    // Also reflect on <html lang> for a11y + native form controls.
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  const t = (k) => STRINGS[lang]?.[k] ?? STRINGS.en[k] ?? k;
  const setLang = (l) => setLangState(l);

  return (
    <I18nCtx.Provider value={{ lang, t, setLang }}>{children}</I18nCtx.Provider>
  );
};

export const useI18n = () => useContext(I18nCtx);

export const LANGS = [
  { id: "en", label: "English", short: "EN" },
  { id: "fr", label: "Français", short: "FR" },
  { id: "ht", label: "Kreyòl Ayisyen", short: "HT" },
  { id: "es", label: "Español", short: "ES" },
];
